<template>
    <div class="prescription-component" :style="overflowStyle">
        <component :is="card ? 'card' : 'div'">
            <!-- RX SEARCH FILTER-->
            <div v-if="showSearchBar" class="mb-2">
                <SearchComponent v-model="prescription" :search-u-r-l="`/Prescription`" @input="fetchPrescription" />
                <b-button variant="info" @click="newPrescription" :disabled="patient?.deceased">New Rx</b-button>
                <hr />
            </div>

            <EntityAlertsComponent ref="entity-alerts" :patient="patient" :prescription="prescription"
                :prescriber="doctor" :drug="drug" :drug-source="drug?.source" :store-id="prescription?.storeID"
                :fill-date="prescription?.fillDate" :rx-status="statusCode" :expiration-date="expiration"
                :additional-alerts="additionalAlerts" class="pb-3">
                <template v-if="!isBatchMode" #default>
                    <h1 class="m-0">Prescription Details</h1>
                </template>
                <template v-if="prescription.rxNumber" #accessory>
                    <div class="d-flex align-items-center mr-4" style="gap: 8px;">
                        <b-button size="sm"
                            :variant="prescription.isUrgent ? 'warning' : 'outline-warning'"
                            @click="toggleUrgent">
                            <i class="fas fa-exclamation-triangle"></i>
                            {{ prescription.isUrgent ? 'Urgent' : 'Mark Urgent' }}
                        </b-button>
                        <b-link v-b-modal.prescription-contacts-modal>Contacts <i class="fas fa-phone" /></b-link>
                        <history-modal v-model="prescription" />
                    </div>
                </template>
            </EntityAlertsComponent>

            <DispensingErrorAlertComponent v-for="(err, index) in dispenseErrors" :key="err.id"
                v-model="dispenseErrors[index]" :buttonTitles="dispenseErrorAlertButtonTitles"
                @onStatusChange="fetchDispenseError" />

            <!-- Ship to Prescriber Banner -->
            <ShipToPrescriberBanner :visible="isDrugShipToPrescriber" />

            <!-- MedSync Banner -->
            <MedSyncBanner
                v-if="patient && patient.id"
                ref="medsync-banner"
                :patient-id="patient.id"
                :rx-id="prescription.rxID"
                @view-details="handleViewMedSyncDetails"
                @add-to-medsync="handleAddToMedSync" />

            <b-row v-show="!!(prescription && prescription.rxNumber)">
                <b-col>
                    <b-alert v-if="patient && patient.deceased" :show="true" variant="danger">
                        <i class="fas fa-exclamation-triangle"></i>
                        <strong> Patient is deceased.</strong>
                        Refills cannot be created for deceased patients.
                    </b-alert>
                    <b-alert v-if="newRefillMode" v-model="notAllowNewRefill" variant="warning">
                        This prescription <b>is not refillable</b>.
                        <span v-if="prescription && !prescription.hasBeenShipped">
                            Previous fill has not been shipped yet.
                        </span>
                    </b-alert>
                    <span v-if="newRefillMode">
                        Save changes to create new refill <strong>Rx #</strong>{{ nextRefillRxId }}
                    </span>
                    <span v-else>
                        <strong>Rx #</strong>{{ prescriptionIdentifier }}
                        <i v-if="prescription.isAutoRefillEnrolled" class="fa fa-redo text-info ml-1"
                            v-b-tooltip.hover title="Auto-Refill Enrolled" style="font-size: 0.8em;" />
                    </span>

                    <div v-if="isBatchMode &&
                        prescriptionsInBatch &&
                        prescriptionsInBatch.length > 1
                    ">
                        <b>In Batch: </b>
                        <span>({{ batchIndex + 1 }} / {{ prescriptionsInBatch.length }})</span>
                    </div>
                    <a href="#" class="ml-2 pull-right"
                        @click.prevent="togglePrescriptionFormLayout">
                        <span>{{ prescriptionFormLayout === 'compact' ? 'Compact' : 'Standard' }}</span> <i :class="prescriptionFormLayout === 'compact' ? 'fas fa-compress-alt' : 'fas fa-expand-alt'" />
                    </a>
                    <router-link v-if="patient?.id" :to="{ name: 'PatientDashboard', params: { id: patient.id } }"
                        class="ml-2 pull-right">
                        <span>Go To Profile</span> <i class="fas fa-user" />
                    </router-link>
                    <router-link
                        v-if="patient?.id && $permissions.some(p => p.permissionCodeName === 'Patient Dashboard/Create Order')"
                        :to="{ name: 'Order', params: { patientId: patient.id } }" class="ml-2 pull-right">
                        <span>New Order</span> <i class="fas fa-plus-circle" />
                    </router-link>
                    <router-link
                        :to="{ name: 'Tasks', params: { taskId: 0 }, query: { patientId: prescription.patientID, prescriptionId: prescription.rxID } }"
                        class="pull-right ml-2">
                        New Task <i class="fas fa-check-circle" />
                    </router-link>
                    <a v-if="$permissions.some(p => p.permissionCodeName === 'Patient Dashboard/Create PAP')" href="#"
                        class="pull-right" @click.prevent="createPAPApplication">
                        Create PAP Application <i class="fas fa-file" />
                    </a>
                </b-col>
            </b-row>

            <b-row v-if="prescription && prescription.rxNumber">
                <b-col>
                    <b-button v-if="!prescription.imageID && !prescription.escriptID" v-b-modal="'add-image'"
                        class="text-left" variant="danger">Add Image
                    </b-button>
                    <b-button v-if="prescription.imageID && !prescription.escriptID" v-b-modal="'add-image'"
                        class="text-left mr-2" variant="danger">Replace Image
                    </b-button>
                    <b-button
                        v-if="hasImageEditOrAnnotatePermission && prescription.imageID && !prescription.escriptID"
                        :disabled="!prescription.rxNumber && !validFormForBilling"
                        :title="annotationDisabledTooltip"
                        variant="warning"
                        @click="openPdfAnnotator">
                        <i class="fas fa-edit"></i> Edit Image
                    </b-button>
                </b-col>
                <b-col class="text-right">
                    <!-- GENERAL RX OPTIONS -->
                    <span v-show="prescription.isActive" class="text-right" style="display: inline-block">
                        <EscriptButtonComponent v-model="prescription" :escript="escript" />
                    </span>
                    <b-button v-if="allowNewRefill && !refillBatchMode" :disabled="newRefillMode || patient?.deceased" variant="info"
                        @click="newRefill">
                        New Refill
                    </b-button>

                    <span
                        v-show="(prescription && (prescription.status || prescription.status === 0) && prescription.status != 4)">
                        <b-button v-if="!!(
                            prescription &&
                            prescription.rxNumber &&
                            prescription.rfNumber != null
                        )
                        " v-b-modal="'transfer-rx'" :disabled="disableInternalTransfer" variant="success">
                            Internal Transfer
                        </b-button>
                        <b-button v-b-modal="'external-transfer-modal'" variant="success">
                            External Transfer
                        </b-button>
                    </span>
                    <span>
                        <b-button v-show="showRefillAuthRequest" v-b-modal="'request-refill-auth-fax'" :disabled="!(
                            prescription &&
                            prescription.rxNumber &&
                            prescription.rfNumber != null
                        ) || patient?.deceased
                            " variant="info">
                            Refill Authorization
                        </b-button>
                        <b-dropdown v-if="hasContactPrescriberAccess" text="Contact Prescriber" variant="info" right>
                            <b-dropdown-item v-if="hasRxClarificationAccess" v-b-modal="'request-rx-clarification-fax'" :disabled="!(
                                prescription &&
                                prescription.rxNumber &&
                                prescription.rfNumber != null
                            )
                                ">
                                RX Clarification
                            </b-dropdown-item>
                            <b-dropdown-item v-if="hasAlternateRxAccess" v-b-modal="'request-alternate-rx-fax'" :disabled="!(
                                prescription &&
                                prescription.rxNumber &&
                                prescription.rfNumber != null
                            )
                                ">
                                Request Alternate Medication
                            </b-dropdown-item>
                            <b-dropdown-item v-if="hasProviderCommunicationsSendPermission"
                                @click="openProviderCommunicationModal"
                                :disabled="!prescription || !doctor || !doctor.npiNumber || !patient">
                                Provider Communication
                            </b-dropdown-item>
                            <b-dropdown-divider v-if="filteredCustomContactPrescriptionTemplates.length" />
                            <b-dropdown-item v-for="template in filteredCustomContactPrescriptionTemplates"
                                :key="template.id"
                                @click="customContactPrescriberTemplate = template; $bvModal.show('custom-prescriber-fax')"
                                :disabled="!(
                                    prescription &&
                                    prescription.rxNumber &&
                                    prescription.rfNumber != null
                                )
                                    ">
                                {{ template.name }}
                            </b-dropdown-item>
                        </b-dropdown>
                    </span>
                    <span v-if="prescription &&
                        prescription.rxNumber &&
                        prescription.drugSource === 2
                        " class="d-inline-flex" style="gap: 8px;">
                        <b-dropdown v-if="!isLoadingPrinters" :text="prettyPrinterName(selectedPrinter)" variant="outline-primary" size="sm">
                            <b-dropdown-item-button :active="!selectedPrinter"
                                @click="selectedPrinter = null">
                                <i class="fa fa-building"></i>
                                Store Default Printer
                            </b-dropdown-item-button>
                            <b-dropdown-item-button v-for="p in localPrinters" :key="p.name"
                                :active="p.name === selectedPrinter?.name" @click="selectedPrinter = p">
                                <i class="fa fa-print"></i>
                                {{ prettyPrinterName(p) }}
                                <div class="d-flex" style="gap: 6px;">
                                    <small v-if="p.default">Local Default</small>
                                    <small v-if="p.status !== 'Unknown'">{{ p.status }}</small>
                                </div>
                            </b-dropdown-item-button>
                            <b-dropdown-item disabled v-if="!hasLocalPrinters">
                                <i class="fa fa-info-circle" />
                                To print to local printers, install<br>the Scriptly Client Service
                            </b-dropdown-item>
                        </b-dropdown>
                        <b-button variant="info" @click="printCompoundIngredients" :disabled="isPrintingIngredients" size="sm">
                            <i class="fa fa-flask"></i> Print Ingredients
                        </b-button>
                    </span>
                    <span v-show="prescription &&
                        prescription.rxNumber &&
                        prescription.pV2ID &&
                        !newRefillMode
                        ">
                        <b-button variant="danger" @click="cancelPV2"> Cancel PV2 </b-button>
                    </span>
                    <span v-show="prescription && prescription.rxNumber && prescription.isVoid">
                        <b-button variant="danger" @click="unVoid"> Unvoid </b-button>
                    </span>
                </b-col>
            </b-row>

            <b-row>
                <!-- PRESCRIPTION IMAGE -->
                <b-col id="prescription-image" class="ml-1" cols="12" md="5">
                    <!-- Action buttons and received date scroll away -->
                    <div class="actions">
                        <b-button v-if="queueItemID" variant="success" @click="showDocuStore = true" class="m-0">
                            Save as Patient Document
                        </b-button>
                        <b-button v-if="queueItemID" variant="danger" @click="markAsNotAScript" class="m-0">
                            Not A Script
                        </b-button>
                        <b-button v-show="showChopperButton" v-b-modal.pdf-choppa variant="info" class="m-0">
                            Split Document
                            <pdf-chopper-modal id="pdf-choppa" :image-id="prescription.imageID"
                                :has-saved-prescription="!!prescription.rxNumber"
                                @splitComplete="handleSplitComplete" v-model="pdfChopperVisible" />
                        </b-button>
                        <template v-if="prescription.escriptID">
                            <b-button v-if="prescription.rxNumber" @click="redrawEscript" class="m-0">Redraw
                                Escript
                            </b-button>
                            <b-button variant="warning" v-b-modal.all-escripts class="m-0">
                                All Escript Images
                                <escript-list-modal v-model="prescription.rxID" id="all-escripts" />
                            </b-button>
                            <b-button
                                v-if="hasImageEditOrAnnotatePermission && !isCancelRxActive"
                                :disabled="!prescription.rxNumber && !validFormForBilling"
                                :title="annotationDisabledTooltip"
                                variant="warning"
                                @click="openPdfAnnotator"
                                class="m-0">
                                <i class="fas fa-edit"></i> Edit Image
                            </b-button>
                        </template>
                    </div>

                    <div v-if="escriptReceivedDisplay" class="small mt-1 mb-2">
                        <i class="fas fa-clock"></i> {{ escriptReceivedDisplay }}
                    </div>

                    <!-- Sticky image section -->
                    <div class="image-wrapper">
                        <ImageComponent ref="prescriptionImage" :escriptID="cancelRxEscriptID || prescription.escriptID"
                            :imageID="prescription.imageID" :rxNumber="prescription.rxNumber"
                            class="script-image" />

                        <!-- Typing/Verification Info -->
                        <div class="typing-info mt-2">
                            <div v-if="prescription.createdBy">
                                <b>Typed By</b>
                                <span class="name">{{ prescription.createdBy?.split('@')[0] }}</span>
                                <span class="date">{{ formattedDateAndTime(prescription.created) }}</span>
                            </div>
                            <div v-if="prescription.pV1ID">
                                <b>PV1 By</b>
                                <span class="name">{{ prescription.pv1By?.split('@')[0] }}</span>
                                <span class="date">{{ formattedDateAndTime(prescription.pv1) }}</span>
                            </div>
                            <div v-if="prescription.pV2ID">
                                <b>Verified By</b>
                                <span class="name">{{ prescription.pv2By?.split('@')[0] }}</span>
                                <span class="date">{{ formattedDateAndTime(prescription.pv2) }}</span>
                            </div>
                        </div>
                    </div>

                    <HardCopyPrescriptionsComponent
                        :imageID="prescription.imageID"
                        :currentStoreID="prescription.storeID"
                        :currentRxNumber="prescription.rxNumber"
                        :currentRfNumber="prescription.rfNumber"
                        @count-changed="siblingScriptCount = $event"
                    />
                </b-col>
                <b-col>
                    <!-- PRESCRIPTION FORM -->
                    <PrescriptionFormFields
                        v-if="!showPatientForm && !showDoctorForm && !editPatientForm && !editDoctorForm && !showDocuStore && !showPharmacyForm"
                        ref="prescriptionForm"
                        :layout="prescriptionFormLayout"
                        :form-state="prescriptionFormState"
                        :store.sync="store"
                        :patient.sync="patient"
                        :doctor.sync="doctor"
                        :program.sync="program"
                        :drug.sync="drug"
                        :origin-code.sync="originCode"
                        :status-code.sync="statusCode"
                        :delivery-code.sync="deliveryCode"
                        :daw-code.sync="dawCode"
                        :substatus-code.sync="substatusCode"
                        :treatment-type-code.sync="treatmentTypeCode"
                        :dispensed-quantity.sync="dispensedQuantity"
                        :expiration.sync="expiration"
                        :written-date.sync="writtenDate"
                        :next-touch-date-local.sync="nextTouchDateLocal"
                        :next-touch-date-manual-override.sync="nextTouchDateManualOverride"
                        :has-active-claims="hasActiveClaims"
                        @preset-changed="onCompoundPresetChanged"
                    />

                    <PatientFormComponent v-if="editPatientForm" :button-titles="patientButtonTitles"
                        :escript-response-patient="patient" :escript-diagnoses="escriptDiagnoses"
                        :escript-allergies="escriptAllergiesList" create-new-patient hide-insurance
                        @close="editPatientForm = false" @created="onPatientCreated" />

                    <PatientFormComponent v-if="showPatientForm" :button-titles="patientButtonTitles"
                        :create-new-patient="true" :escript-response-patient="escriptPatient"
                        :escript-diagnoses="escriptDiagnoses" :escript-allergies="escriptAllergiesList"
                        hide-insurance
                        @close="showPrescriptionForm('hidePatientForm')" @created="onPatientCreated" />

                    <DoctorFormComponent v-if="editDoctorForm" :button-titles="doctorButtonTitles"
                        :escript-response-prescriber="doctor" create-new-doctor @close="editDoctorForm = false"
                        @created="onDoctorCreated" />

                    <DoctorFormComponent v-if="showDoctorForm" :button-titles="doctorButtonTitles"
                        :create-new-doctor="true" :escript-response-prescriber="escriptPrescriber"
                        @close="showPrescriptionForm('hideDoctorForm')" @created="onDoctorCreated" />

                    <pharmacy-detail-page v-if="showPharmacyForm"
                        :button-titles="{ save: 'Save Changes', cancel: 'Close', new: '', clear: '' }" new-mode
                        @close="onPharmacyFormClose" @created="onPharmacyCreate" />

                    <PatientDocumentsForm v-if="showDocuStore" ref="patientDocumentsForm" :queueItem="queueItem"
                        @close="showPrescriptionForm()" @on-save="handlePatientDocSaved" />
                </b-col>
            </b-row>

            <hr />
            <b-alert v-model="showDrugAlert" dismissible variant="danger">
                <b>Drug Memo:</b> {{ drugAlert }}
            </b-alert>
            <b-alert v-model="showAlertIsOutOfState" variant="warning">
                <b>Prescription cannot be filled at this store.</b> Please transfer to appropriate
                store licensed in patient's state.
            </b-alert>
            <b-alert v-model="showInactiveDrugAlert" :variant="isHubMode ? 'warning' : 'danger'">
                <template v-if="isHubMode">
                    <b>Selected drug is not active.</b> In Hub Mode, prescriptions with inactive drugs can only be saved as On Hold, Void, or Transfer Out.
                </template>
                <template v-else>
                    <b>Selected drug is not active</b>. Please select an active drug.
                </template>
            </b-alert>
            <b-alert v-model="showAgeAlert" variant="warning">
                <b>Prescription Fill Date too old, Manager Intervention Required</b>
            </b-alert>
            <!-- Custom Questions-->
            <b-row v-for="cat in drugCategories" :key="cat.id">
                <b-col>
                    <QuestionaryComponent :ref="`questionary_${cat.id}`" :categoryID="+cat.id"
                        :disable="!prescription.isActive" :prescription="prescription"
                        @saved-answers="questionarySaved">
                    </QuestionaryComponent>
                </b-col>
            </b-row>

            <h4 ref="insuranceBillingSection">Insurance Billing</h4>
            <b-row>
                <b-col>
                    <InsuranceBillingComponent ref="insuranceComponent" :billOnlyMultilfills="isMultiFill"
                        :defaultPriceSchedule="program && program.priceSchedule" :disabled="disableBillingComponent"
                        :drugLicenseType="drugLicenseType" :newMode="newMode || newRefillMode" :patient="patient"
                        :prescription="prescription" :isMedSyncShortFill="medSyncShortFillData?.isMedSyncShortFill || false"
                        @clearRx="clearForm" @onClaimsLoaded="claimsLoaded"
                        @startSurescriptsElectronicPriorAuthorization="startSurescriptsElectronicPriorAuthorization
                        " @startSurescriptsElectronicPriorAuthorizationExpedited="startSurescriptsElectronicPriorAuthorizationExpedited
                        " @save-with-plan="saveAndBill" @rx-needs-refresh="fetchPrescription(prescription)"
                        @rx-transfer-refresh="rxTransferRefresh" @pricing-updated="updatePricing" />
                </b-col>
            </b-row>
            <hr />
            <!-- Hard Copy Note -->
            <template v-if="validFormForBilling && hasHardcopyPermission && (prescription.imageID || prescription.escriptID) && !isCancelRxActive">
                <b-row>
                    <b-col>
                        <fg-input label="Hardcopy Note">
                            <textarea v-model="addNote" class="textarea-autosize form-control" placeholder="Enter note text to add to the prescription image..." />
                        </fg-input>
                        <div class="d-flex align-items-center">
                            <b-button :disabled="!validFormForBilling ||
                                (prescription && !prescription.isActive) ||
                                !hasHardcopyPermission ||
                                !addNote.trim()" variant="success" @click="openQuickAnnotate">
                                Add Hardcopy Note
                            </b-button>
                            <b-form-checkbox v-model="keepHardcopyNote" switch class="ml-3 mb-0">
                                Keep note after save
                            </b-form-checkbox>
                        </div>
                    </b-col>
                </b-row>
                <hr />
            </template>
            <!-- Patient Notes -->
            <b-row v-if="patient && patient.id">
                <b-col>
                    <NotesForm v-if="patient" :entityId="+patient.id" :entityType="patientEntityType"
                        :saveButtonLabel="'Add Patient Note'" :textBoxLabel="'Patient Note'"
                        @saved="refreshPatientNotes" />
                    <NotesList ref="patientNotesList" :entityId="+patient.id" :entityType="patientEntityType"
                        :pageSize="5" />
                </b-col>
            </b-row>

            <hr v-if="prescription" />

            <b-overlay :show="saving">
                <NewClearSaveButtons :titles="buttonTitles" :disable-new="disableNew" :disable-clear="disableClear"
                    :disable-save="disableSave" :disable-cancel="saving" show-clear-modal @cancel-clicked="cancel"
                    @clear-clicked="undoChanges" @new-clicked="saveAndContinue" @save-clicked="save"
                    @save-and-stay-clicked="saveAndStay" />
            </b-overlay>
        </component>

        <DebugViewComponent header="">
            <b-row>
                <b-col>
                    disableFieldsReason: {{ disableFieldsReason }}
                </b-col>
            </b-row>
            <b-row>
                <b-col>
                    disableBillingReason: {{ disableBillingReason }}
                </b-col>
            </b-row>
        </DebugViewComponent>
        <!-- Error resolve confirmation -->
        <b-modal id="modal_save_confirmation" hide-footer title="Was the error resolved?">
            <checkbox v-model="wasResolved">
                Confirming that the reported error was resolved
            </checkbox>
            <br />
            <NotesForm :bodyRequired="false" :entityId="dispenseError && +dispenseError.id"
                :entityType="dispenseErrorEntityType"
                :placeholder="'Introduce a note with additional information (not required)'"
                :prefixNote="`${wasResolved ? '** RESOLVED **' : ''}`" :saveButtonLabel="saveButtonLabel" :silent="true"
                @input="noteChanged" @saved="modalSaveAndResolve" />
        </b-modal>

        <b-modal id="add-image" title="Add Image" centered-title="xl">
            <b-row>
                <b-col>
                    <vue-dropzone id="dropzone" ref="myVueDropzone" :options="dropzoneOptions"
                        @vdropzone-success="dropzoneUploadSuccess" @vdropzone-sending="dropzoneSending" />
                </b-col>
            </b-row>
        </b-modal>

        <b-modal id="transfer-rx" hide-footer no-close-on-backdrop title="Transfer Rx to:">
            <b-row>
                <b-col>
                    <SearchComponent :id="newStore && +newStore.id" v-model="newStore" :label="`New Store`"
                        :prepopulate="true" :search-u-r-l="`/Store`" />
                </b-col>
            </b-row>
            <b-row>
                <b-col>
                    <b-button block class="mt-3" @click="$bvModal.hide('transfer-rx')">
                        Close
                    </b-button>
                </b-col>
                <b-col>
                    <b-button block class="mt-3" variant="success" @click="transferRx()">
                        Transfer
                    </b-button>
                </b-col>
            </b-row>
        </b-modal>

        <!-- External Transfer Modal -->
        <ExternalTransferModal
            :prescriptions="[prescription]"
            :store="store"
            :patient="patient"
            @transferred="onTransferCompleted" />

        <b-modal id="prescription-contacts-modal" title="Prescription Contacts" hide-footer>
            <b-table :items="prescriptionContacts" :fields="prescriptionContactFields" no-provider-sorting />
        </b-modal>

        <PatientRxHistoryModalComponent id="prescription-details-rx-history" ref="patient_rx_history_modal"
            :patient="patient" />

        <FaxToPhysicianFormModal :drug="drugAsDrug" :patient="patient" :prescriber="doctor" :prescription="prescription"
            :store="store" modal-id="request-refill-auth-fax" @saved="updateAlerts" />

        <FaxToPhysicianFormModal :drug="drugAsDrug" :form-type="faxFormTypeRequestEnum.RxClarification"
            :patient="patient" :prescriber="doctor" :prescription="prescription" :store="store"
            modal-id="request-rx-clarification-fax" @saved="updateAlerts" />

        <AlternateRxFormModal :drug="drugAsDrug" :form-type="faxFormTypeRequestEnum.AlternateRxRequest"
            :patient="patient" :prescriber="doctor" :prescription="prescription" :store="store"
            modal-id="request-alternate-rx-fax" @saved="updateAlerts" />

        <FaxToPhysicianFormModal :drug="drugAsDrug" :custom-template="customContactPrescriberTemplate"
            :patient="patient" :prescriber="doctor" :prescription="prescription" :store="store"
            modal-id="custom-prescriber-fax" @saved="updateAlerts" />

        <DiscontinueEquivalentScriptsModal
            v-model="showEquivalentModal"
            :patient-id="prescription.patientID"
            :package-id="prescription.packageID"
            :current-store-id="prescription.storeID"
            :current-rx-number="prescription.rxNumber"
            :initial-equivalent-prescriptions="equivalentPrescriptionsData"
            @discontinued="onEquivalentDiscontinued"
            @skipped="onEquivalentSkipped"
        />

        <patient-notes-modal-component v-if="prescription && prescription.patientID"
            :entity-id="prescription.patientID" />
        <doctor-notes-modal-component v-if="prescription && prescription.prescriberID"
            :entity-id="prescription.prescriberID" />
        <drug-notes-modal-component v-if="prescription && prescription.packageID" :entity-id="prescription.packageID"
            :drug-source="prescription.drugSource" />

        <b-modal id="cancel-reason-modal" title="Cancel Transfer" @ok="cancelTransferMyRxWithReason" @hidden="cancelReason = ''">
            <b-form-group label="Reason" label-for="reason-input" >
                <b-form-input id="reason-input" v-model="cancelReason"/>
            </b-form-group>
        </b-modal>

        <!-- MedSync Modals -->
        <MedSyncDetailsModal
            v-if="patient && patient.id"
            ref="medsync-details-modal"
            :patient-id="patient.id" />

        <AddToMedSyncModal
            v-if="patient && patient.id"
            ref="add-to-medsync-modal"
            :patient-id="patient.id"
            :rx-id="prescription.rxID"
            @added="handleMedSyncAdded" />

        <!-- PDF Annotation Modal -->
        <PdfAnnotationModal
            ref="pdf-annotation-modal"
            :image-id="annotationImageId"
            :escript-id="annotationEscriptId"
            :initial-text="quickAnnotateText"
            :auto-place="isQuickAnnotate"
            :sibling-count="siblingScriptCount"
            modal-id="prescription-pdf-annotation"
            title="Edit Prescription Image"
            :require-permission="true"
            :show-success-notification="false"
            @saved="onPdfAnnotationSaved"
            @closed="onPdfAnnotationClosed" />

        <ProviderCommunicationModal v-if="providerCommunicationModalVisible && patient && providerCommunicationContext"
            :thread="null"
            :initiate-context="providerCommunicationContext"
            :patient-id="+patient.id"
            @close="closeProviderCommunicationModal" />

    </div>
</template>

<script lang="ts">
import { Component, Mixins, Prop, Ref, Watch } from "vue-property-decorator";
import Axios, { AxiosError, CancelTokenSource } from "axios";
import Vue2Dropzone from "vue2-dropzone";
import { NotificationOptions } from "@/util/NotificationOptionsPresets";
import NewClearSaveButtons from "@/components/NewClearSaveButtons.vue";
import EnumSelector from "@/components/EnumSelector.vue";
import DispensingErrorAlertComponent from "@/components/DispensingError/DispensingErrorAlertComponent.vue";
import NotesForm from "@/components/Note/NotesForm.vue";
import NotesList from "@/components/Note/NotesList.vue";
import InsuranceBillingComponent from "@/components/InsurancePlan/InsuranceBillingComponent.vue";
import ImageComponent from "@/components/Prescription/ImageComponent.vue";
import HardCopyPrescriptionsComponent from "@/components/Prescription/HardCopyPrescriptionsComponent.vue";
import EquivalentDrugsComponent from "@/components/Drug/EquivalentDrugsComponent.vue";
import QuestionaryComponent from "@/components/Questionary/QuestionaryComponent.vue";
import { Lock, LockHandler } from "@/mixins/LockHandler";
import AutoModeHotkeyMixin from '@/mixins/AutoModeHotkeyMixin';
import PhoneFormatMixin from "@/mixins/PhoneFormatMixin";
import {
    DAWCode,
    OriginCode,
    Prescription,
    PrescriptionDispenseItem,
    RefillStatusCode,
    RxDeniedSubStatusId,
    TreatmentType,
} from "@/models/Prescription";
import { DeliveryCode } from "@/models/DeliveryCode";
import { Prescriber } from "@/models/Prescriber";
import { Patient } from "@/models/Patient";
import { Program } from "@/models/Program";
import { DispenseError } from "@/models/DispenseError";
import { Store, StoreCapabilities, StoreTimeZone } from "@/models/Store";
import { QueueItem } from "@/models/QueueItem";
import { Drug, DrugStatus } from "@/models/Drug/Drug";
import { DrugCategory } from "@/models/DrugCategory";
import { Escript, EscriptResponse, EscriptResponseMessage } from "@/models/Escript";
import { FaxType as FaxToPhysicianFormType } from "@/models/PrescriberFollowUpCase";
import { Location } from "vue-router";
import { DatePicker } from "element-ui";
import { Note } from "@/models/Note";
import PatientFormComponent from "@/components/Patient/PatientFormComponent.vue";
import PatientRxHistoryModalComponent from "@/components/Patient/PatientRxHistoryModalComponent.vue";
import FaxToPhysicianFormModal from "@/components/Prescription/FaxToPhysicianFormModal.vue";
import AlternateRxFormModal from "@/components/Prescription/AlternateRxFormModal.vue";
import DiscontinueEquivalentScriptsModal from "@/components/Prescription/DiscontinueEquivalentScriptsModal.vue";
import type { EquivalentPrescription } from "@/models/EquivalentPrescription";
import DoctorFormComponent from "@/components/Doctor/DoctorFormComponent.vue";
import Checkbox from "@/components/Inputs/Checkbox.vue";
import PatientDocumentsForm from "@/components/Patient/PatientDocumentsForm.vue";
import DoctorFollowUpCasesAlertComponent from "@/components/Doctor/DoctorFollowUpCasesAlertComponent.vue";
import moment from "moment";
import { Claim } from "@/models/Claim";
import DirectionsComponent from "@/components/Prescription/DirectionsComponent.vue";
import EscriptButtonComponent from "@/components/Prescription/EscriptButtonComponent.vue";
import SearchComponent from "@/components/SearchComponent.vue";
import { ValidatorGroup } from "@/util/ValidatorGroup";
import { AutoRefillSubscription } from "@/models/AutoRefillSubscription";
import { DrugProduct, DrugSource } from "@/models/Drug/DrugProduct";
import { NonGSDDDrug } from "@/models/Drug/NonGSDDDrug";
import { GSDDPatientDiagnosis } from "@/models/GSDD";
import { CompoundDrug } from "@/models/Drug/CompoundDrug";
import CompoundIngredientsDisplay from "@/components/Compound/CompoundIngredientsDisplay.vue";
import { PharmacyRecord } from "@/models/Pharmacy";
import PharmacyDetailPage from "@/pages/Pharmacy/PharmacyDetailPage.vue";
import PharmacistDetailPage from "@/pages/Pharmacist/PharmacistDetailPage.vue";
import { PharmacyBenefitManager } from "@/models/PharmacyBenefitManager";
import EscriptListModal from "@/components/Prescription/EscriptListModal.vue";
import { BvTableCtxObject } from "bootstrap-vue";
import DebugViewComponent from "@/components/DebugViewComponent.vue";
import { OrderStatus } from "@/models/Order";
import { EntityType } from "@/models/EntityType";
import PdfChopperModal from "@/components/Prescription/PdfChopperModal.vue";
import HistoryModal from "@/components/Modals/HistoryModal.vue";
import { PrescriberContactTemplate } from "@/models/FaxTemplates";
import HelpPopup from "@/components/HelpPopup.vue";
import DoctorNotesModalComponent from "@/components/Note/DoctorNotesModalComponent.vue";
import PatientNotesModalComponent from "@/components/Note/PatientNotesModalComponent.vue";
import DrugNotesModalComponent from "@/components/Note/DrugNotesModalComponent.vue";
import DrugInfoComponent from "@/components/Drug/DrugInfoComponent.vue";
import { PatientDocument } from "@/models/PatientDocument";
import EntityAlertsComponent, { Alert } from "@/components/EntityAlertsComponent.vue";
import RxTransferListModalComponent from "@/components/Patient/RxTransferListModalComponent.vue";
import ProviderCommunicationModal from "@/components/ProviderCommunications/ProviderCommunicationModal.vue";
import { providerCommunicationsPermissions } from "@/services/providerCommunicationsService";
import SearchPrescriptionDetailComponent from "@/components/Prescription/SearchPrescriptionDetailComponent.vue";
import PrescriptionFormFields from "@/components/Prescription/PrescriptionFormFields.vue";
import { PrescriptionFormState } from "@/models/PrescriptionFormState";
import ExternalTransferModal from "@/components/Transfer/ExternalTransferModal.vue";
import MedSyncBanner from "@/components/MedSync/MedSyncBanner.vue";
import MedSyncDetailsModal from "@/components/MedSync/MedSyncDetailsModal.vue";
import AddToMedSyncModal from "@/components/MedSync/AddToMedSyncModal.vue";
import PdfAnnotationModal from "@/components/PdfAnnotationModal.vue";

let processingInsurance = false;
let previousRoute: any;

@Component({
    computed: {
        PharmacyBenefitManager() {
            return PharmacyBenefitManager;
        },
    },
    beforeRouteLeave(_to, _from, next) {
        if (processingInsurance) {
            alert("Please wait until insurance processing has finished");
            next(false);
        } else {
            next();
        }
    },
    beforeRouteEnter(_to: any, from: any, next: any) {
        previousRoute = from;
        next();
    },
    name: "PrescriptionDetailsPage",
    components: {
        ProviderCommunicationModal,
        SearchPrescriptionDetailComponent,
        PrescriptionFormFields,
        DrugNotesModalComponent,
        PatientNotesModalComponent,
        DoctorNotesModalComponent,
        HistoryModal,
        PdfChopperModal,
        DebugViewComponent,
        EscriptListModal,
        PharmacistDetailPage,
        DirectionsComponent,
        NewClearSaveButtons,
        EnumSelector,
        InsuranceBillingComponent,
        ImageComponent,
        HardCopyPrescriptionsComponent,
        [DatePicker.name]: DatePicker,
        PatientFormComponent,
        DoctorFormComponent,
        DispensingErrorAlertComponent,
        NotesForm,
        NotesList,
        Checkbox,
        EquivalentDrugsComponent,
        EscriptButtonComponent,
        QuestionaryComponent,
        PatientRxHistoryModalComponent,
        PatientDocumentsForm,
        FaxToPhysicianFormModal,
        AlternateRxFormModal,
        DiscontinueEquivalentScriptsModal,
        DoctorFollowUpCasesAlertComponent,
        VueDropzone: Vue2Dropzone,
        SearchComponent,
        PharmacyDetailPage,
        HelpPopup,
        DrugInfoComponent,
        EntityAlertsComponent,
        ExternalTransferModal,
        CompoundIngredientsDisplay,
        MedSyncBanner,
        MedSyncDetailsModal,
        AddToMedSyncModal,
        PdfAnnotationModal,
        ShipToPrescriberBanner: () => import("@/components/Order/ShipToPrescriberBanner.vue"),
    },
})
export default class PrescriptionDetailsPage extends Mixins(LockHandler, AutoModeHotkeyMixin, PhoneFormatMixin) {
    @Prop() prescriptionsInBatch!: Array<Prescription>;
    @Prop({ default: true, type: Boolean }) card!: Boolean;
    @Ref('prescriptionForm') prescriptionForm!: PrescriptionFormFields;
    @Ref("doctor-followup-alerts") doctorFollowupAlerts!: DoctorFollowUpCasesAlertComponent;
    @Ref('rxTransfersModalComponent') rxTransfersModalComponent!: RxTransferListModalComponent;
    @Ref('insuranceComponent') insuranceComponent!: InsuranceBillingComponent;
    @Ref("entity-alerts") entityAlertsComponent!: EntityAlertsComponent;
    @Ref('medsync-banner') medSyncBanner!: MedSyncBanner;
    @Ref('medsync-details-modal') medSyncDetailsModal!: MedSyncDetailsModal;
    @Ref('add-to-medsync-modal') addToMedSyncModal!: AddToMedSyncModal;
    @Ref('pdf-annotation-modal') pdfAnnotationModal!: PdfAnnotationModal;
    @Ref('prescriptionImage') prescriptionImageComponent!: ImageComponent;

    // Proxy getter for child component ref (used by searchProgramParamsChanged watcher)
    get programSearchComponent(): SearchComponent | undefined {
        return this.prescriptionForm?.programSearchComponent;
    }

    errorLoadingDrugDetails = false;

    directionsTranslations: { paraphrases: { id: string, token: string }[], token: string } = {} as any;
    selectedTranslation: string = "";
    loadingDirections: boolean = false;

    // Enumerations
    protected originCodes = OriginCode;
    protected dawCodes = DAWCode;
    protected deliveryCodes = DeliveryCode;
    protected treatmentTypeCodes = TreatmentType;
    protected doctorButtonTitles = {
        new: "",
        clear: "",
        save: "Save Doctor",
        cancel: "Close Doctor Form",
    };
    protected prescriptionFormLayout: "default" | "compact" = "default";

    get prescriptionFormLayoutSetting(): string | undefined {
        return (this.$root as any).$data.userSettings?.['prescription-form-layout'];
    }

    @Watch("$root.$data.userSettings", { immediate: true })
    onUserSettingsLoaded() {
        this.applyPrescriptionFormLayout();
    }

    @Watch("prescriptionFormLayoutSetting")
    onPrescriptionFormLayoutSettingChanged() {
        this.applyPrescriptionFormLayout();
    }

    private applyPrescriptionFormLayout() {
        const saved = this.prescriptionFormLayoutSetting;
        if (saved === "compact" || saved === "default") {
            this.prescriptionFormLayout = saved;
        }
    }

    togglePrescriptionFormLayout() {
        this.prescriptionFormLayout = this.prescriptionFormLayout === "compact" ? "default" : "compact";
        (this as any).$saveUserSetting?.("prescription-form-layout", this.prescriptionFormLayout);
    }

    protected imageURLs: string[] = [];
    protected imageError: boolean = false;
    protected faxFormTypeRequestEnum = FaxToPhysicianFormType;
    protected equivalentDrugs: Drug.EquivalentDrug[] = [];
    private cancelReason: string = "";

    protected patientButtonTitles = {
        new: "",
        clear: "",
        save: "Save Patient",
        cancel: "Close Patient Form",
    };

    //Props
    @Prop({ default: false }) private refillBatchMode!: boolean;
    @Prop() private medSyncShortFillData?: {
        adjustedDaysSupply: number;
        adjustedDispensed: number;
        isMedSyncShortFill: boolean;
        medSyncAnchorDate: Date;
    };
    // Entities
    private prescription: Prescription = new Prescription();
    private store: Store = new Store();
    private newStore: Store = new Store();
    private patient: Patient = new Patient();
    private doctor: Prescriber = new Prescriber();
    private patientPrescribers: Prescriber[] = [];
    private program: Program = new Program();
    private doctorProgram: Program = new Program();
    private drug: DrugProduct = new Drug();
    private escript: Escript = new Escript();
    private claims: Array<Claim> = [];
    private drugCategories: Array<DrugCategory> = [];
    private prescriptionLock: Lock | null = null;
    protected queueItem: QueueItem | null = null;
    private savedIngestionQueueItemID: string | null = null;
    private savedEscriptID: string | undefined = undefined;
    private cancelRxEscriptID: string | null = null;
    private savedProgramTransfer: string | undefined = undefined;
    private lastFill: Prescription = new Prescription();
    private autoRefillSubscription: AutoRefillSubscription = new AutoRefillSubscription();
    private drugAlert: string = "";
    protected showDrugChange = false;
    private dispenseError: DispenseError = new DispenseError();
    private dispenseErrors: Array<DispenseError> = [];
    private errorNote: Note = {} as Note;
    protected patientEntityType = EntityType.Patient;
    protected dispenseErrorEntityType = EntityType.DispenseError;
    private escriptResponseMessage: EscriptResponseMessage | null = null;
    private originalFillDate: Date = new Date();
    protected originPharmacy: PharmacyRecord = new PharmacyRecord();
    private tmrxCancelReason: string = "";
    private fetchEscriptCancelTokenSource: CancelTokenSource | null = null;
    private pendingScrollToBilling = false;

    // Arguments - now passed as route props
    @Prop() imageID?: string;
    @Prop() escriptID?: string;
    @Prop() queueItemID?: string;
    @Prop() programTransfer?: string;

    //Flags
    private newMode = false;
    private fromIngestion = false;
    private wasResolved: boolean = false;
    private newRefillMode = false;
    private isScheduleClassDrug = false;
    //View values
    private dispensedQuantity: number = 0;
    private originCode: number = 0;
    private statusCode: number = 0;
    private deliveryCode: number = 0;
    private dawCode: number = 0;
    private substatusCode: number | null = null;
    private treatmentTypeCode: number | null = null;
    private nextTouchDateLocal: string = "";
    private nextTouchDateManualOverride: boolean = false;
    private settingViewValues: boolean = false;
    private requireNextTouchDateOnHold: boolean = false;
    private statusCodes = RefillStatusCode;
    protected drugStatuses: string[] = [
        "Inactive",
        "Active",
        "Active and Preferred",
        "Blocked From Fills",
    ];
    // Buttons config
    private buttonTitles = {
        new: "",
        clear: "Clear",
        save: "Save Changes",
        cancel: "",
    };

    protected disableFieldsReason: string = "";
    protected disableBillingReason: string = "";

    protected saveButtonLabel: string = "Save changes";
    //Notes
    private addNote = "";
    private keepHardcopyNote = false;
    private quickAnnotateText: string | null = null;
    private isQuickAnnotate = false;
    private siblingScriptCount = 0;
    protected editPatientForm = false;
    protected showPatientForm = false;
    protected showDocuStore = false;
    protected showDoctorForm = false;
    protected showEquivalentModal = false;
    protected equivalentPrescriptionsData: EquivalentPrescription[] = [];
    protected checkingEquivalentDrugs = false; // transient state flag - true while the equivalent drug API call is in progress
    protected enableEquivalentDrugCheck = false; // configuration setting - when true, runs the equivalent drug check on save
    protected enforcePackageSizeBilling = false;
    protected showPharmacyForm = false;
    protected editDoctorForm = false;
    private drugDetails: any = null;
    protected drugInventory: any = null;
    protected nextRefillRxId = "";
    private fieldsPending = "";
    //--------------
    protected isClaimsLoading = true;
    private initialDispensedQty = 0;
    private expiration: Date | null = null;
    private writtenDate: Date | any = null;
    private enableExpirationDateEstimation = true;
    protected loadingDrugDetails = false;
    private batchIndex: number = -1;
    private globalPreventRedirection = false;
    // validation
    private validationClasses: {
        quantity: string;
        dispense: string;
        refills: string;
        daySupply: string;
        fillDate: string;
        writtenDate: string;
        expirationDate: string;
    } = {
        quantity: "",
        dispense: "",
        refills: "",
        daySupply: "",
        fillDate: "",
        writtenDate: "",
        expirationDate: "",
    };
    private saving: boolean = false;
    private stayOnPageAfterSave: boolean = false;

    protected pdfChopperVisible: boolean = false;
    private prescriptionImagePages: number = 0;

    private showDispensingFields = true;
    private disableFutureWrittenDates = false;
    private transferMyRxStatus = "";
    protected programSetFromTransfer: boolean = false;
    private isPrintingIngredients = false;
    private localPrinters: any[] = [];
    private selectedPrinter: any | null = null;
    private isLoadingPrinters = false;

    @Watch('prescription.transferMyRxId')
    async getTransferMyRxStatus() {
        this.transferMyRxStatus = "";

        if (!this.prescription?.transferMyRxId) return;

        const newResp = await Axios.get<any>(`/api/TransferMyRx/${this.prescription.transferMyRxId}`);
        if (!newResp.data) return;

        const json = JSON.parse(newResp.data.jsonBody);
        console.log("newResp", newResp);
        console.log("json", json);

        this.transferMyRxStatus = json.status;
        return json.status;
    }

    async cancelTransferMyRx() {
        try {
            await Axios.post(`/api/TransferMyRx/cancel/${this.prescription.transferMyRxId}`,{}, {
                params: {
                    reason: this.tmrxCancelReason
                }
            });
        } catch (err) {
            const error = err as AxiosError;
            if (error.response?.status === 418) {
                this.showLockedMessage(
                    EntityType.Prescription,
                    this.prescription?.id?.toString(),
                    this.prescription?.toString(),
                    error.response.data
                );
            } else {
                this.$notification(NotificationOptions.error(error));
            }
        }
    }

    async cancelTransferMyRxWithReason() {
        try {
            await Axios.post(`/api/TransferMyRx/cancel/${this.prescription.transferMyRxId}`,{}, {
                params: {
                    reason: this.cancelReason
                }
            });
        } catch (err) {
            const error = err as AxiosError;
            if (error.response?.status === 418) {
                this.showLockedMessage(
                    EntityType.Prescription,
                    this.prescription?.id?.toString(),
                    this.prescription?.toString(),
                    error.response.data
                );
            } else {
                this.$notification(NotificationOptions.error(error));
            }
        }
    }

    async loadLocalPrinters() {
        try {
            this.isLoadingPrinters = true;
            const response = await this.$http.get("http://localhost:7274/print/devices");
            this.localPrinters = response.data;

            // Restore last selected printer from localStorage
            const lastPrinter = localStorage.getItem("active-ingredients-printer");
            if (lastPrinter) {
                this.selectedPrinter = this.localPrinters?.find(p => p.name === lastPrinter);
            }
        } catch {
            // Printer service not running - ignore and default to queue printing
            this.localPrinters = [];
        } finally {
            this.isLoadingPrinters = false;
        }
    }

    get hasLocalPrinters() {
        return !!this.localPrinters.length;
    }

    prettyPrinterName(printer: any) {
        if (!printer) return "Store Default Printer";
        if (!printer.serverName) return printer.name;
        return printer.name.replace(printer.serverName, "").replace(/\\/g, "");
    }

    async printCompoundIngredients() {
        try {
            this.isPrintingIngredients = true;

            const rxId = `${this.prescription.storeID}-${this.prescription.rxNumber}-${this.prescription.rfNumber}`;
            const localPrint = !!this.selectedPrinter;

            const response = await Axios.get(`/api/Print/${rxId}/compound-ingredients`, {
                params: { localPrint }
            });

            if (localPrint && response.data.toPrint?.length > 0) {
                await this.sendToLocalPrint(response.data.toPrint);
            }

            this.$notification(NotificationOptions.notificationOptionsPreset(
                localPrint
                    ? 'Compound ingredients sent to local printer successfully'
                    : 'Compound ingredients sent to print queue successfully',
                NotificationOptions.NotificationTypes.success
            ));
        } catch (err) {
            const error = err as AxiosError;
            console.error('Error printing compound ingredients:', error);
            this.$notification(NotificationOptions.error(error));
        } finally {
            this.isPrintingIngredients = false;
        }
    }

    async getAuthToken(ensureFresh: boolean = false): Promise<string | null> {
        try {
            // Check auth provider type
            const authProviderType = (this as any).$authProviderType;

            if (authProviderType === "Cognito") {
                // Get Cognito token - pass ensureFresh to force refresh if token is expiring soon
                const { getToken } = await import("@/util/cognitoAuth");
                return await getToken(ensureFresh);
            } else {
                // Get MSAL/OIDC token - pass ensureFresh to force token refresh if needed
                const MicrosoftAuth = (await import("@/util/microsoftAuth")).default;
                const token = await MicrosoftAuth.fetchToken(ensureFresh);
                return token.accessToken;
            }
        } catch (err) {
            console.error("Failed to get auth token", err);
            return null;
        }
    }

    async sendToLocalPrint(jobsToPrint: any) {
        try {
            const printerName = this.selectedPrinter.name;
            const apiBaseUrl = this.$http.defaults.baseURL?.replace("/api", "");

            // Get a fresh auth token to pass to the client service
            const authToken = await this.getAuthToken(true);

            // Generate unique job ID for this print request
            const jobId = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

            const localRequest = {
                printerName,
                jobs: jobsToPrint,
                apiBaseUrl,
                markAsPrinted: false,
                authToken,
                jobId,
            };

            // Send print request to local client service
            await this.$http.post("http://localhost:7274/print", localRequest);

            // Save selected printer to localStorage
            localStorage.setItem("active-ingredients-printer", printerName);
        } catch (error) {
            console.error('Error sending to local printer:', error);
            throw error;
        }
    }

    get showChopperButton(): boolean {
        return this.prescriptionImagePages > 1;
    }

    get escriptReceivedDisplay(): string | null {
        if (!this.escript?.created) return null;

        const date = new Date(this.escript.created);
        if (isNaN(date.getTime())) return null;

        const storeTimeZoneToIana: Record<number, string> = {
            [StoreTimeZone.PacificStandardTime]: "America/Los_Angeles",
            [StoreTimeZone.MountainStandardTime]: "America/Denver",
            [StoreTimeZone.USMountainStandardTime]: "America/Phoenix",
            [StoreTimeZone.CentralStandardTime]: "America/Chicago",
            [StoreTimeZone.EasternStandardTime]: "America/New_York",
            [StoreTimeZone.USEasternStandardTime]: "America/New_York",
        };

        const browserIana = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const storeIana = this.store.timeZone != null ? storeTimeZoneToIana[this.store.timeZone] : null;
        const primaryTz = storeIana || browserIana;

        const formatInTz = (tz: string, includeDate: boolean) => {
            const options: Intl.DateTimeFormatOptions = {
                timeZone: tz,
                hour: "numeric",
                minute: "2-digit",
                second: "2-digit",
                hour12: true,
                timeZoneName: "short",
            };
            if (includeDate) {
                options.year = "numeric";
                options.month = "long";
                options.day = "numeric";
            }
            const parts = new Intl.DateTimeFormat("en-US", options).formatToParts(date);
            const get = (type: string) => parts.find(p => p.type === type)?.value ?? "";
            const tzAbbrev = get("timeZoneName");
            if (includeDate) {
                return `${get("month")} ${get("day")}, ${get("year")} ${get("hour")}:${get("minute")}:${get("second")} ${get("dayPeriod")} (${tzAbbrev})`;
            }
            return `${get("hour")}:${get("minute")}:${get("second")} ${get("dayPeriod")} (${tzAbbrev})`;
        };

        const primaryDisplay = formatInTz(primaryTz, true);

        if (storeIana && storeIana !== browserIana) {
            const browserDisplay = formatInTz(browserIana, false);
            const storeTimeOnly = formatInTz(storeIana, false);
            if (storeTimeOnly !== browserDisplay) {
                return `Received: ${primaryDisplay} / ${browserDisplay}`;
            }
        }

        return `Received: ${primaryDisplay}`;
    }

    get overflowStyle() {
        if (this.pdfChopperVisible) return "overflow:hidden;";
        return "overflow:inherit;";
    }

    get drugNdc(): string {
        return this.drugDetails?.ndc ?? "";
    }

    get drugPackageSize(): string {
        return (this.drugDetails && `${this.drugDetails.packageSize} ${this.drugDetails.billingUnit}`) || "";
    }

    protected drugLastFilled: string = "";

    @Watch('prescriptionsInBatch')
    @Watch('patient.id')
    @Watch('prescription.rxID')
    async fetchDrugLastFilled(): Promise<string> {
        let rxId = this.prescription.rxID;
        const drugSource = this.prescription.drugSource;
        const drugId = this.prescription.packageID;
        this.drugLastFilled = "";
        if (this.patient.id == undefined || drugSource == undefined || !this.prescription.hasFullId) {
            return this.drugLastFilled;
        }
        try {
            const newResp = await Axios.get(`/api/Patient/${this.patient.id}/drug-history/${drugSource}/${drugId}/${rxId}`);
            // Only set drugLastFilled if the response contains a valid date
            if (newResp.data) {
                const momentDate = moment(newResp.data);
                if (momentDate.isValid()) {
                    this.drugLastFilled = momentDate.format("L");
                }
            }
        } catch (err) {
            //swallow
        }
        return this.drugLastFilled;
    }

    get drugStatus(): string {
        if (!isNaN(this.drug.activeStatus)) return this.drugStatuses[this.drug.activeStatus];

        if (!this.drugDetails) return "";
        if (!isNaN(this.drugDetails?.activeStatus)) return "";
        return this.drugStatuses[this.drugDetails.activeStatus];
    }

    get drugInStock(): boolean {
        return this.drugInventory && this.drugInventory.quantity - this.drugInventory.allocated > 0;
    }

    get drugOutOfStock(): boolean {
        return this.drugInventory && this.drugInventory.quantity - this.drugInventory.allocated <= 0;
    }

    get isDrugShipToPrescriber(): boolean {
        return this.drugCategories?.some(
            (c) => c.name?.toLowerCase() === "ship to prescriber"
        ) ?? false;
    }

    get drugAsDrug(): Drug {
        return this.drug as Drug;
    }

    get drugSearchParams(): { getProgram: boolean; drugSource?: number } {
        const params: { getProgram: boolean; drugSource?: number } = { getProgram: true };
        // Check drug.source first (available immediately on selection),
        // then fall back to prescription.drugSource (for existing prescriptions)
        const drugSource = this.drug?.source ?? this.prescription?.drugSource;
        if (drugSource !== undefined && drugSource !== null) {
            params.drugSource = drugSource;
        }
        return params;
    }

    get dispenseErrorAlertButtonTitles(): any {
        return {
            resolve: "Resolve",
            awaitingResponse: "Awaiting Response",
        };
    }

    get showDrugAlert() {
        return this.drugAlert.length > 0;
    }

    get escriptPatient() {
        return this.escriptResponseMessage?.patient ?? null;
    }

    get escriptDiagnoses(): GSDDPatientDiagnosis[] {
        const drugs = this.escriptResponseMessage?.drugs;
        if (!drugs?.diagnosisPrimaryCode) return [];

        const makeDiagnosis = (code: string, description: string): GSDDPatientDiagnosis => ({
            icD10Code: code,
            icD10Description: description || '',
            primaryPrograms: [],
            created: new Date(),
            patientId: 0,
            id: 0,
            createdBy: '',
            removed: null,
            removedBy: '',
            isUtc: false,
        });

        const diagnoses = [makeDiagnosis(drugs.diagnosisPrimaryCode, drugs.diagnosisPrimaryDescription)];

        if (drugs.diagnosisSecondaryCode) {
            diagnoses.push(makeDiagnosis(drugs.diagnosisSecondaryCode, drugs.diagnosisSecondaryDescription));
        }

        return diagnoses;
    }

    get escriptAllergiesList() {
        return this.escriptResponseMessage?.allergies ?? [];
    }

    get escriptPrescriber() {
        return this.escriptResponseMessage?.prescriber ?? null;
    }

    get hasSuperAdminPermission() {
        return this.$permissions.some(p => p.permissionCodeName.toLowerCase() === "Super Admin/Prescription".toLowerCase());
    }

    get shouldBlockFutureWrittenDates() {
        return this.disableFutureWrittenDates && !this.hasSuperAdminPermission;
    }

    get disableSave(): boolean {
        if (this.hasSuperAdminPermission) return this.saving || this.checkingEquivalentDrugs;
        if (this.saving || this.checkingEquivalentDrugs) return true;
        if (this.patient?.deceased && (this.newRefillMode || this.newMode)) return true;
        return (!this.validForm || !(this.prescription && this.prescription.isActive));
    }

    get disableNew(): boolean {
        return this.disableSave;
    }

    get disableClear(): boolean {
        if (this.saving) return true;
        return !(this.newMode || (this.prescription && this.prescription.rxNumber));
    }

    get disableInternalTransfer(): boolean {
        if (this.hasClaims) return true;
        if (this.isClaimsLoading) return true;
        if (!this.prescription) return true;
        if (!this.prescription.rxNumber) return true;
        // noinspection RedundantIfStatementJS
        if (isNaN(Number(this.prescription.rfNumber))) return true;
        return false;
    }

    get showSearchBar(): boolean {
        return !(
            this.prescription?.rxNumber ||
            this.newMode ||
            this.fromIngestion ||
            this.isBatchMode ||
            this.routeRxId
        );
    }

    get shouldLockEscriptProcessingStore(): boolean {
        if (this.hasSuperAdminPermission) return false;

        return this.disableFields || !!(this.prescription && this.prescription.rxNumber) || ((!!this.escript.id) && (this.store.id > 0));
    }

    get routeRxId(): string | any {
        let res = null;
        const storeID = Number(this.$route.params.storeID);
        const rxNumber = Number(this.$route.params.rxNumber);
        const rfNumber = Number(this.$route.params.rfNumber);
        const rxId = this.$route.params.rxId;
        if (!isNaN(storeID) && !isNaN(rxNumber) && !isNaN(rfNumber)) {
            res = `${storeID}-${rxNumber}-${rfNumber}`;
        } else if (rxId) res = rxId;
        return res;
    }

    get drugLicenseType(): string {
        return this.drugDetails?.licenseType || "unknown";
    }

    get routeDispenseErrorId(): number | null {
        let res = null;
        if (this.$route.params.dispenseErrorId) {
            res = Number(this.$route.params.dispenseErrorId);
        } else if (this.dispenseError?.id) res = Number(this.dispenseError?.id);
        return res;
    }

    get hasClaims(): boolean {
        return this.claims.length > 0;
    }

    get disablePatient(): boolean {
        if (!!this.prescription.rfNumber) return true;

        if (this.hasSuperAdminPermission) return false;

        if (this.prescription.orders?.some(o => o.status == OrderStatus.Created)) return true;
        if (this.prescription.orders?.some(o => o.status == OrderStatus.Processing)) return true;
        if (this.prescription.orders?.some(o => o.status == OrderStatus.PartialPayment)) return true;
        if (this.prescription.orders?.some(o => o.status == OrderStatus.AwaitingPriorAuthorization)) return true;
        if (this.prescription.orders?.some(o => o.status == OrderStatus.ReadyForPickup)) return true;
        if (this.prescription.orders?.some(o => o.status == OrderStatus.ReturnToSender)) return true;

        return false;
    }

    get isUserPrescriptionAdmin() {
        return this.$permissions.some(p => p.permissionCodeName.toLowerCase() === "Admin/Prescription".toLowerCase());
    }

    // Each branch corresponds to at least one self-gated dropdown item, so adding
    // a permission here without a matching inner item would render an empty menu.
    get hasContactPrescriberAccess() {
        return this.$permissions.some(p =>
            p.permissionCodeName === "Pharmacists/Contact Prescriber" ||
            p.permissionCodeName === "Pharmacists/Fax Rx Clarification Request" ||
            p.permissionCodeName === "Pharmacists/Fax Alternate Rx Request" ||
            p.permissionCodeName === "Admin/Prescription" ||
            p.permissionCodeName === "Super Admin/Prescription" ||
            this.hasProviderCommunicationsSendPermission
        );
    }

    get hasProviderCommunicationsSendPermission() {
        return this.$permissions.some(p =>
            p.permissionCodeName === providerCommunicationsPermissions.send
        );
    }

    providerCommunicationModalVisible = false;
    providerCommunicationContext: { prescriberNPI: string; prescriberFirstName: string | null; prescriberLastName: string | null; storeID: number | null; rxNumber: number | null; rfNumber: number | null } | null = null;

    openProviderCommunicationModal() {
        if (!this.doctor?.npiNumber || !this.patient?.id) return;
        this.providerCommunicationContext = {
            prescriberNPI: this.doctor.npiNumber,
            prescriberFirstName: this.doctor.firstName ?? null,
            prescriberLastName: this.doctor.lastName ?? null,
            storeID: this.prescription?.storeID ?? null,
            rxNumber: this.prescription?.rxNumber ?? null,
            rfNumber: this.prescription?.rfNumber ?? null,
        };
        this.providerCommunicationModalVisible = true;
    }

    closeProviderCommunicationModal() {
        this.providerCommunicationModalVisible = false;
        this.providerCommunicationContext = null;
    }

    get hasRxClarificationAccess() {
        return this.$permissions.some(p =>
            p.permissionCodeName === "Pharmacists/Contact Prescriber" ||
            p.permissionCodeName === "Pharmacists/Fax Rx Clarification Request" ||
            p.permissionCodeName === "Admin/Prescription" ||
            p.permissionCodeName === "Super Admin/Prescription"
        );
    }

    get hasAlternateRxAccess() {
        return this.$permissions.some(p =>
            p.permissionCodeName === "Pharmacists/Contact Prescriber" ||
            p.permissionCodeName === "Pharmacists/Fax Alternate Rx Request" ||
            p.permissionCodeName === "Admin/Prescription" ||
            p.permissionCodeName === "Super Admin/Prescription"
        );
    }

    get notSelectableStatus(): RefillStatusCode[] {
        const baseStatuses = [
            RefillStatusCode.TransferOut,
            RefillStatusCode.TransferIn,
            RefillStatusCode.Canceled,
        ];

        const hasVoidPermission = this.$permissions.some(p =>
            p.permissionCodeName === "Pharmacists/Void Prescription"
        );

        const hasDiscontinuePermission = this.$permissions.some(p =>
            p.permissionCodeName === "Pharmacists/Discontinue Prescription"
        );

        if (!hasVoidPermission && this.prescription.status !== RefillStatusCode.Void) {
            baseStatuses.push(RefillStatusCode.Void);
        }

        if (!hasDiscontinuePermission && this.prescription.status !== RefillStatusCode.Discontinued) {
            baseStatuses.push(RefillStatusCode.Discontinued);
        }

        // Hub mode with inactive drug: only allow OnHold, Void, and TransferOut
        if (this.isHubMode && this.isDrugActive === false) {
            const allowedStatuses = [
                RefillStatusCode.OnHold,
                RefillStatusCode.Void,
                RefillStatusCode.TransferOut,
            ];
            const allStatuses = Object.values(RefillStatusCode).filter(v => typeof v === 'number') as RefillStatusCode[];
            for (const status of allStatuses) {
                if (!allowedStatuses.includes(status) && !baseStatuses.includes(status)) {
                    baseStatuses.push(status);
                }
            }
        }

        return baseStatuses;
    }

    get prescriptionFormState(): PrescriptionFormState {
        return {
            // Prescription entity (property mutations propagate via reference)
            // Note: store, patient, doctor, program, drug are passed as separate .sync props
            prescription: this.prescription,
            originPharmacy: this.originPharmacy,

            // Enum code sets
            originCodes: this.originCodes,
            dawCodes: this.dawCodes,
            deliveryCodes: this.deliveryCodes,
            treatmentTypeCodes: this.treatmentTypeCodes,
            statusCodes: this.statusCodes,
            notSelectableStatus: this.notSelectableStatus,
            applicableSubStatuses: this.applicableSubStatuses,

            // Flags
            disableFields: this.disableFields,
            disableBillingComponent: this.disableBillingComponent,
            disableQuantity: this.disableQuantity,
            disableRefills: this.disableRefills,
            disableProgramChange: this.disableProgramChange,
            disablePatient: this.disablePatient,
            shouldLockEscriptProcessingStore: this.shouldLockEscriptProcessingStore,
            newMode: this.newMode,
            fromIngestion: this.fromIngestion,
            newRefillMode: this.newRefillMode,
            showDispensingFields: this.showDispensingFields,
            rxWasBilled: this.rxWasBilled,
            requireNextTouchDateOnHold: this.requireNextTouchDateOnHold,
            minNextTouchDate: this.minNextTouchDate,

            // Display values
            storeLocationInformation: this.storeLocationInformation,
            patientDOB: this.patientDOB,
            isNPIInvalid: this.isNPIInvalid,
            showDrugChange: this.showDrugChange,
            sigIsSmartSig: this.sigIsSmartSig,
            transferMyRxStatus: this.transferMyRxStatus,
            fillsLeft: this.fillsLeft,
            doctorProgram: this.doctorProgram,
            validationClasses: this.validationClasses,

            // Drug details
            drugDetails: this.drugDetails,
            drugNdc: this.drugNdc,
            drugPackageSize: this.drugPackageSize,
            drugStatus: this.drugStatus,
            drugLastFilled: this.drugLastFilled,
            drugInStock: this.drugInStock,
            drugOutOfStock: this.drugOutOfStock,
            drugInventory: this.drugInventory,
            drugAsDrug: this.drugAsDrug,
            imageURLs: this.imageURLs,
            imageError: this.imageError,
            loadingDrugDetails: this.loadingDrugDetails,
            errorLoadingDrugDetails: this.errorLoadingDrugDetails,
            equivalentDrugs: this.equivalentDrugs,

            // Search params
            prescriptionDrugSearchParams: this.prescriptionDrugSearchParams,
            drugSearchParams: this.drugSearchParams,
            searchStoreGetParams: this.searchStoreGetParams,
            searchProgramGetParams: this.searchProgramGetParams,
            autoselectStore: this.autoselectStore,
            autoselectProgram: this.autoselectProgram,
            prescriberResultsModifier: this.prescriberResultsModifier,

            // Directions state
            directionsTranslations: this.directionsTranslations,
            selectedTranslation: this.selectedTranslation,
            loadingDirections: this.loadingDirections,

            // Dispensing items
            dispenseItems: this.dispenseItems,

            // Callbacks
            patientUpdate: (value: any) => this.patientUpdate(value),
            doctorUpdate: (value: any) => this.doctorUpdate(value),
            programUpdate: (value: any) => this.programUpdate(value),
            changeDispense: (num: number) => this.changeDispense(num),
            updateDispenseItem: (field: string, index: number, value: any) => this.updateDispenseItem(field, index, value),
            startEditPatient: () => this.startEditPatient(),
            openRxHistory: () => this.openRxHistory(),
            onShowPatientForm: () => { this.showPatientForm = true; },
            onShowDoctorForm: () => { this.showDoctorForm = true; },
            onEditDoctorForm: () => { this.editDoctorForm = true; },
            onShowPharmacyForm: () => { this.showPharmacyForm = true; },
            onSelectedTranslationChange: (value: string) => { this.selectedTranslation = value; },
            translateDirections: () => this.translateDirections(),
            handleDaysSupplyCalculated: (value: number) => this.handleDaysSupplyCalculated(value),
            equivalentDrugsSearched: (drugs: any) => this.equivalentDrugsSearched(drugs),
            onImageErrorChange: (value: boolean) => { this.imageError = value; },
            onOriginPharmacyChange: (value: PharmacyRecord | null) => {
                this.originPharmacy = value || new PharmacyRecord();
                this.prescription.originPharmacyId = value?.id;
            },
        };
    }

    get disableFields(): boolean {
        if (this.hasSuperAdminPermission) return false;
        this.disableFieldsReason = "";
        const lockedForUser = !this.prescriptionLock;
        const noEntityLoaded = !(this.prescription && this.prescription.rxNumber);
        const isCanceled = this.prescription.status == RefillStatusCode.Canceled;
        let ManagerCond = false;
        if (!this.fromIngestion) {
            let dateNow: Date = new Date();
            let dateFillDate: Date = new Date(this.prescription.fillDate);
            let timeInMilliseconds: number = dateNow.getTime() - dateFillDate.getTime();
            let daysBetweenDates: number = Math.ceil(timeInMilliseconds / (1000 * 60 * 60 * 24));
            ManagerCond =
                !this.isUserPrescriptionAdmin &&
                daysBetweenDates > 15 &&
                this.prescription.status != RefillStatusCode.OnHold &&
                this.prescription.status != RefillStatusCode.AwaitingInsurance;
        }

        if (((lockedForUser || noEntityLoaded) && !this.newMode && !this.fromIngestion)) {
            this.disableFieldsReason = "((lockedForUser || noEntityLoaded) && !this.newMode && !this.fromIngestion)";
            return true;
        }

        if (this.hasClaims) {
            this.disableFieldsReason = "this.hasClaims";
            return true;
        }
        if (isCanceled) {
            this.disableFieldsReason = "isCancelled";
            return true;
        }
        if (!this.prescription.isActive) {
            this.disableFieldsReason = "!this.prescription.isActive";
            return true;
        }
        if (this.prescription.isRx30BeforeApril && !this.newRefillMode) {
            this.disableFieldsReason = "this.prescription.isRx30BeforeApril && !this.newRefillMode";
            return true;
        }
        if (ManagerCond) {
            this.disableFieldsReason = "ManagerCond";
            return true;
        }

        // noinspection UnnecessaryLocalVariableJS
        const shouldDisable =
            ((lockedForUser || noEntityLoaded) && !this.newMode && !this.fromIngestion) ||
            this.hasClaims ||
            isCanceled ||
            !this.prescription.isActive ||
            (this.prescription?.isRx30BeforeApril && !this.newRefillMode) ||
            ManagerCond;

        // console.log("L1", ((lockedForUser || noEntityLoaded) && !this.newMode && !this.fromIngestion));
        // console.log("L2", this.hasClaims);
        // console.log("L3", isCanceled);
        // console.log("L4", !this.prescription.isActive);
        // console.log("L5", (this.prescription?.isRx30BeforeApril && !this.newRefillMode));
        // console.log("L6", ManagerCond);
        // console.log("Main", shouldDisable);
        return shouldDisable;
    }

    get disableRefills() {
        if (this.hasSuperAdminPermission) return false;
        return this.disableFields || (this.hasShipped && this.prescription.rfNumber != 0);
    }

    get disableQuantity() {
        if (this.hasSuperAdminPermission) return false;
        if (this.disableFields) return true;
        return !!this.prescription.rfNumber;
    }

    get disableBillingComponent(): boolean {
        if (this.hasSuperAdminPermission) return false;

        this.disableBillingReason = "";

        if ((!this.prescription.isActive && !this.newMode)) {
            this.disableBillingReason = "(!this.prescription.isActive && !this.newMode)";
            return true;
        }
        if ((this.prescription.isRx30BeforeApril && !this.newRefillMode)) {
            this.disableBillingReason = "(this.prescription.isRx30BeforeApril && !this.newRefillMode)";
            return true;
        }
        const notRefillable = !this.allowNewRefill && this.newRefillMode;
        if (notRefillable) {
            this.disableBillingReason = "notRefillable";
            return true;
        }
        if (this.statusCode == this.statusCodes.OnHold) {
            this.disableBillingReason = "this.statusCode == this.statusCodes.OnHold";
            return true;
        }
        if (this.isOutOfState) {
            this.disableBillingReason = "this.isOutOfState";
            return true;
        }
        if (this.statusCode == this.statusCodes.AwaitingInsurance) {
            this.disableBillingReason = "this.statusCode == this.statusCodes.AwaitingInsurance";
            return true;
        }
        const isShippedOrVerified = (this.prescription.hasBeenShipped || this.prescription.hasBeenVerified) && !this.newRefillMode;
        if (isShippedOrVerified) {
            this.disableBillingReason = "(this.prescription.hasBeenShipped || this.prescription.hasBeenVerified) && !this.newRefillMode";
            return true;
        }
        if (this.isOlderThan14Days) {
            this.disableBillingReason = "this.isOlderThan14Days";
            return true;
        }

        return (
            (!this.prescription.isActive && !this.newMode) ||
            (this.prescription.isRx30BeforeApril && !this.newRefillMode) ||
            notRefillable ||
            this.statusCode == this.statusCodes.OnHold ||
            this.isOutOfState ||
            this.isOlderThan14Days ||
            this.statusCode == this.statusCodes.AwaitingInsurance ||
            isShippedOrVerified
        );
    }

    get disableProgramChange(): boolean {
        if (this.hasSuperAdminPermission) return false;

        return this.disableFields || (this.programSetFromTransfer && !this.hasOverrideTransferProgramPermission);
    }

    get hasOverrideTransferProgramPermission() {
        return this.$permissions.some(p => p.permissionCodeName.toLowerCase() === "Processing/Override Transfer Program".toLowerCase());
    }

    get hasShipped(): boolean {
        return !!(this.prescription && this.prescription.rxNumber && !this.prescription.rfNumber);
    }

    get fillsLeft(): number {
        if (!this.prescription) return 0;
        return this.prescription.refills;
        //if (!this.prescription.rfNumber) return this.prescription.refills;
        //return this.prescription.refills - this.prescription.rfNumber;
    }

    get hasHardcopyPermission() {
        return this.$permissions.some(p => p.permissionCodeName === "Maintenance/Hardcopy Note");
    }

    get hasEditImagePermission() {
        return this.$permissions.some(p => p.permissionCodeName === "Maintenance/Edit Image");
    }

    get hasImageEditOrAnnotatePermission() {
        return this.hasHardcopyPermission || this.hasEditImagePermission;
    }

    get annotationImageId(): number | null {
        // For escripts, imageId will be null - the modal will use escriptId instead
        // For regular prescriptions, use the imageID
        if (this.prescription?.escriptID) {
            return null;
        }
        return this.prescription?.imageID || null;
    }

    get annotationEscriptId(): number | null {
        return this.prescription?.escriptID || null;
    }

    get isCancelRxActive(): boolean {
        return !!this.cancelRxEscriptID;
    }

    get validFormForBilling() {
        // Form validation
        let valGrpEntities: ValidatorGroup = new ValidatorGroup([
            new ValidatorGroup.Validator("Store", !!this.prescription.storeID),
            new ValidatorGroup.Validator("Patient", !!this.prescription.patientID),
            new ValidatorGroup.Validator("Drug", !!this.prescription.packageID),
            new ValidatorGroup.Validator("Doctor", !!this.prescription.prescriberID),
            new ValidatorGroup.Validator("Program", !!this.prescription.programID),
        ]);
        const entitiesWereSelected = valGrpEntities.evaluate();

        let valGrpQuantities: ValidatorGroup = new ValidatorGroup([
            new ValidatorGroup.Validator("Day Supply", this.prescription.daySupply?.toString() != '' && this.prescription.daySupply >= 0),
            new ValidatorGroup.Validator("Quantity", this.prescription.quantity?.toString() != '' && this.prescription.quantity >= 0),
            new ValidatorGroup.Validator("Dispensed", this.prescription.dispensed?.toString() != '' && this.prescription.dispensed >= 0),
            new ValidatorGroup.Validator("Origin", this.prescription.originCode?.toString() != '' && this.originCode > 0),
            new ValidatorGroup.Validator("Refills", this.prescription.refills?.toString() != '' && this.prescription.refills >= 0),
        ]);
        const quantitiesWereIntroduced = valGrpQuantities.evaluate();

        let writtenDateNotInFuture = true;
        if (this.writtenDate) {
            const todayDate = new Date().setHours(0, 0, 0, 0);
            const writtenDateObj = this.writtenDate instanceof Date ? this.writtenDate : new Date(this.writtenDate);
            const compareDate = new Date(writtenDateObj.getFullYear(), writtenDateObj.getMonth(), writtenDateObj.getDate()).setHours(0, 0, 0, 0);
            const dateIsNotInFuture = !(compareDate > todayDate);
            writtenDateNotInFuture = this.shouldBlockFutureWrittenDates ? dateIsNotInFuture : true;
        }

        let valGrpDates: ValidatorGroup = new ValidatorGroup([
            new ValidatorGroup.Validator("Written Date", !!this.writtenDate),
            new ValidatorGroup.Validator("Expiration", !!this.expiration),
            new ValidatorGroup.Validator("Future Written Date", writtenDateNotInFuture),
        ]);
        const datesWereIntroduced = valGrpDates.evaluate();

        const remainingQuantity =
            this.prescription.remainingQuantity +
            (this.prescription.rfNumber == 0 ? 0 : this.initialDispensedQty);

        let valDispense = this.dispensedQuantity <= remainingQuantity;
        if (valDispense) {
            if (this.statusCode < 3 && this.dispensedQuantity == 0) {
                valDispense = false;
            }
        }

        const fillDateBeforeExpiration = moment(this.prescription.fillDate).isBefore(this.expiration);
        const fillDateNotBeforeWrittenDate = !this.prescription.fillDate || !this.writtenDate ||
            moment(this.prescription.fillDate).isSameOrAfter(moment(this.writtenDate), "day");
        const validRefillMode = (this.allowNewRefill && this.newRefillMode) || !this.newRefillMode;
        const packageSizeMultipleValid = !this.isPackageSizeMultipleInvalid();
        let valGrpLongNames: ValidatorGroup = new ValidatorGroup([
            new ValidatorGroup.Validator("Dispensed quantity must be less than total allowed to dispense", this.dispensedQuantity <= remainingQuantity),
            new ValidatorGroup.Validator("Dispensed quantity must be an even multiple of the package size", packageSizeMultipleValid),
            new ValidatorGroup.Validator("Fill date should be before expiration date", fillDateBeforeExpiration),
            new ValidatorGroup.Validator("Fill date cannot be before the written date", fillDateNotBeforeWrittenDate),
            new ValidatorGroup.Validator("Mismatch in Prescriber NPI", !this.isNPIInvalid),
            new ValidatorGroup.Validator("Not Refillable", validRefillMode),
            new ValidatorGroup.Validator("Drug is not active", !!this.isDrugActive || this.isHubMode),
        ]);

        this.fieldsPending = "";
        const allGroups = new ValidatorGroup([...valGrpQuantities.validators, ...valGrpDates.validators, ...valGrpEntities.validators, ...valGrpLongNames.validators]);
        this.fieldsPending = allGroups.concatValidatorNames(false);

        if (!valDispense && quantitiesWereIntroduced) {
            if (this.dispensedQuantity === 0) {
                this.validationClasses.dispense = "has-warning";
                valDispense = true;
            } else {
                this.validationClasses.dispense = "has-danger";
            }
        }

        if (!packageSizeMultipleValid) {
            this.validationClasses.dispense = "has-danger";
        }

        // Set validation styling for the written date
        if (!writtenDateNotInFuture) {
            this.validationClasses.writtenDate = "has-danger";
        } else {
            this.validationClasses.writtenDate = "";
        }

        // Validate NextTouchDate when required for on-hold prescriptions or manual override
        let nextTouchDateValid = true;
        if (this.statusCode == RefillStatusCode.OnHold
            && (this.requireNextTouchDateOnHold || this.nextTouchDateManualOverride)) {
            if (!this.nextTouchDateLocal) {
                nextTouchDateValid = false;
            } else {
                const ntd = moment(this.nextTouchDateLocal);
                nextTouchDateValid = ntd.isValid() && ntd.isAfter(moment());
            }
        }

        // noinspection UnnecessaryLocalVariableJS
        const formIsValid =
            entitiesWereSelected &&
            quantitiesWereIntroduced &&
            datesWereIntroduced &&
            valDispense &&
            packageSizeMultipleValid &&
            fillDateBeforeExpiration &&
            fillDateNotBeforeWrittenDate &&
            validRefillMode &&
            !this.isNPIInvalid &&
            (this.isDrugActive || this.isHubMode) &&
            nextTouchDateValid;

        return formIsValid;
    }

    get annotationDisabledTooltip(): string {
        if (this.isCancelRxActive) {
            return 'CancelRx images are display-only and cannot be annotated.';
        }
        if (this.prescription?.rxNumber || this.validFormForBilling) {
            return ''; // Not disabled, no tooltip needed
        }
        return `Complete required fields first: ${this.fieldsPending || 'Drug, Patient, Store, Doctor, Program'}`;
    }

    get validForm() {
        // Process validation
        const isOnHold = this.statusCode == RefillStatusCode.OnHold && this.substatusCode != null;
        const isAwaitingStatus = this.statusCode == RefillStatusCode.AwaitingInsurance;
        const isVoid = this.statusCode == RefillStatusCode.Void;
        const isDiscontinued = this.statusCode == RefillStatusCode.Discontinued;
        const isFillDateInTheFuture =
            moment(this.prescription.fillDate).isAfter(moment().toDate(), "day") &&
            moment(this.prescription.fillDate).isBefore(this.expiration, "day");

        const processIsValid =
            this.hasClaims ||
            isOnHold ||
            isVoid ||
            isDiscontinued ||
            isFillDateInTheFuture ||
            (this.prescription.isCashedOut && this.statusCode != RefillStatusCode.OnHold) ||
            isAwaitingStatus;

        //Whole validation
        return (
            this.validFormForBilling &&
            processIsValid &&
            (!this.prescription?.isRx30BeforeApril || this.newRefillMode) &&
            (this.isDrugActive || this.isHubMode)
        );
    }

    get isLastFill(): boolean {
        if (!this.lastFill.hasFullId) return true;
        return this.lastFill?.rxID == this.prescription?.rxID || this.newRefillMode;
    }

    get allowNewRefill(): boolean {
        if (this.patient?.deceased) return false;
        return !!(this.prescription?.isRefillable && this.isLastFill); //&& !this.refillBatchMode && !this.newRefillMode
    }

    get notAllowNewRefill(): boolean {
        return !this.allowNewRefill;
    }

    get validBillingInformation() {
        const isFillDateInTheFuture =
            moment(this.prescription.fillDate).isAfter(moment().toDate(), "day") &&
            moment(this.prescription.fillDate).isBefore(this.expiration, "day");

        return (
            this.hasClaims ||
            this.prescription.isCashedOut ||
            this.prescription.status == RefillStatusCode.Void ||
            this.prescription.status == RefillStatusCode.Discontinued ||
            this.prescription.status == RefillStatusCode.AwaitingInsurance ||
            (this.prescription.status == RefillStatusCode.OnHold &&
                this.prescription.subStatus != null) ||
            isFillDateInTheFuture
        );
    }

    get rxWasBilled(): boolean {
        return !!this.claims?.some((cl) => cl.transactionStatus == "Paid");
    }

    get hasActiveClaims(): boolean {
        return !!this.claims?.some((cl) => !cl.reversed);
    }

    get isMultiFill(): boolean {
        return (this.dispensedQuantity || 0) > (this.prescription?.quantity || 0);
    }

    get isOutOfState(): boolean {
        if (!this.patient?.addressState || !this.store?.addressState) return false;
        if (this.patient.addressState == this.store.addressState) return false;
        return !this.store.allowedStates.some(s => this.patient.addresses.some(a => a.state?.toUpperCase().trim() == s));
    }

    get showAlertIsOutOfState(): boolean {
        return this.isOutOfState;
    }

    get isHubMode(): boolean {
        return !!(this.store?.capabilities & StoreCapabilities.HubMode);
    }

    get prescriptionDrugSearchParams(): Record<string, any> {
        if (this.isHubMode) {
            return { onlyActive: false, allSources: true };
        }
        return {};
    }

    get isDrugActive(): boolean | null {
        if (!this.drug?.activeStatus) return null;
        return (
            this.drug?.activeStatus === DrugStatus.DrugStatusEnum.Active ||
            this.drug?.activeStatus === DrugStatus.DrugStatusEnum.ActiveAndPreferred
        );
    }

    get showInactiveDrugAlert(): boolean {
        return this.isDrugActive === false;
    }

    get sigIsSmartSig(): boolean {
        if (!this.escript?.smartSig) return false;
        return this.prescription?.directions === this.escript?.smartSig?.sigText;
    }

    get dropzoneOptions(): any {
        return {
            url: `${Axios.defaults.baseURL}/image/prescription/${this.prescription.storeID}/${this.prescription.rxNumber}/${this.prescription.rfNumber}`,
            thumbnailWidth: 150,
            withCredentials: true,
            paramName: "images",
        };
    }

    get showRefillAuthRequest(): boolean {
        //If we can't fill the next fill, allow a refill auth to go.
        return (
            (this.prescription.remainingQuantity || 0) < (this.prescription.dispensed || 0) * 2 ||
            this.isDrugExpired
        );
    }

    get willAutorefill(): boolean {
        //Is subscribed to autorefill, the current date is more/equals than next process date and this page is in refill mode.
        let nextProcessDateValid = true;
        if (this.autoRefillSubscription?.nextProcessDate) {
            //nextProcessDateValid = moment().isSameOrAfter(this.autoRefillSubscription.nextProcessDate, 'day');
        }
        return !!(this.autoRefillSubscription?.id && (this.newRefillMode || this.currentBatchItemIsExistingFill) && nextProcessDateValid);
    }

    get isDrugExpired() {
        if (!this.expiration) return false;
        // Show refill auth button if prescription expires within 90 days or is already expired
        const expirationDate = moment(this.expiration);
        const daysUntilExpiration = expirationDate.diff(moment(), 'days');
        return daysUntilExpiration <= 90;
    }

    get isOlderThan14Days(): boolean {
        // Users with Admin/Prescription permission can edit scripts regardless of age
        if (this.isUserPrescriptionAdmin || this.newRefillMode) return false;

        const lastFillDate = moment(this.prescription.fillDate);
        const daysOld = moment().diff(lastFillDate, 'days');
        // scripts 14 or more days old require a prescription admin to do adjustments.
        return daysOld >= 14;
    }

    get showAgeAlert(): boolean {
        return this.isOlderThan14Days;
    }

    private onHoldSubStatuses: Array<{ value: number, label: string, active: boolean, offsetValue?: number, offsetUnit?: string }> = [];
    private discontinuedSubStatuses: Array<{ value: number, label: string, active: boolean }> = [];

    get applicableSubStatuses() {
        return this.statusCode == RefillStatusCode.OnHold ? this.onHoldSubStatuses : this.discontinuedSubStatuses;
    }

    get minNextTouchDate() {
        return moment().format("YYYY-MM-DDTHH:mm");
    }

    async getSubStatusConfiguration() {
        try {
            const resp = await this.$http.get("/api/Configuration");
            this.onHoldSubStatuses = resp.data.onHoldSubStatuses.map((ss: any) => ({
                value: ss.id,
                label: ss.name,
                active: ss.active,
                offsetValue: ss.offsetValue,
                offsetUnit: ss.offsetUnit,
            }));
            this.discontinuedSubStatuses = resp.data.discontinuedSubStatuses.map((ss: any) => ({
                value: ss.id,
                label: ss.name,
                active: ss.active,
            }));
            this.requireNextTouchDateOnHold = resp.data.requireNextTouchDateOnHold ?? false;
        } catch {
            //ignore
        }
    }

    get prescriptionIdentifier(): string {
        if (!this.prescription) return "";
        if (
            this.prescription.storeID == null ||
            this.prescription.rxNumber == null ||
            this.prescription.rfNumber == null
        ) {
            return "N/A";
        }
        return `${this.prescription.storeID}-${this.prescription.rxNumber}-${this.prescription.rfNumber}`;
    }

    get storeLocationInformation(): string {
        if (!this.store?.id) return "";
        return `${this.store.addressCity}, ${this.store.addressState}`;
    }

    get isNPIInvalid() {
        //Apply only if we are loading Escript or Program Transfer
        const escriptId = this.escriptID || this.savedEscriptID;
        const programTransfer = this.programTransfer || this.savedProgramTransfer;
        if (!escriptId && !programTransfer) return false;

        //Invalid if there is no prescriber specified in escript or if not equal to the selected doctor.
        return !this.escriptPrescriber || this.escriptPrescriber.npiNumber != this.doctor.npiNumber;
    }

    get searchProgramGetParams() {
        const escriptId = this.escriptID || this.savedEscriptID;
        const programTransfer = this.programTransfer || this.savedProgramTransfer;
        const programToAutoselect =
            (Number(escriptId) || 0) > 0 || (Number(programTransfer) || 0)
                ? this.prescription.programID
                : null;
        return {
            active: true,
            flags: "Autoselect Only",
            discard: true,
            forceProgramID: programToAutoselect || 0,
            filterByTeam: true,
        };
    }

    get searchStoreGetParams() {
        return {
            filterByTeam: true,
        };
    }

    get patientDOB(): string {
        if (!this.patient || !this.patient.dateOfBirth) return "";
        return moment(this.patient.dateOfBirth).format("L");
    }

    get escriptAllergyAlert() {
        const escriptId = this.escriptID || this.savedEscriptID;
        return (
            escriptId &&
            this.escriptResponseMessage &&
            this.escriptResponseMessage.allergies?.length > 0
        );
    }

    get escriptDiagnosisAlert() {
        const escriptId = this.escriptID || this.savedEscriptID;
        return (
            escriptId &&
            this.escriptDiagnoses?.length > 0
        );
    }

    get additionalAlerts() {
        const alerts: Alert[] = [];

        if (this.escriptAllergyAlert) {
            const allergyNames = (this.escriptResponseMessage?.allergies ?? [])
                .map(a => a.drugText || a.adverseEventText)
                .filter(Boolean)
                .join(', ');
            const detail = allergyNames ? `: ${allergyNames}` : '';
            alerts.push({
                name: "escriptAllergy",
                title: "Add Escript Allergies",
                message: `Add allergies found on escript before saving prescription${detail}`,
                icon: "fa fa-plus",
                variant: "red"
            });
        }

        if (this.escriptDiagnosisAlert) {
            const codes = this.escriptDiagnoses.map(d => d.icD10Code).join(', ');
            alerts.push({
                name: "escriptDiagnosis",
                title: "Escript Diagnosis",
                message: `ICD10 codes found on escript: ${codes}`,
                icon: "fa fa-stethoscope",
                variant: "info"
            });
        }

        return alerts;
    }

    get isBatchOver(): boolean {
        return this.batchIndex + 1 > (this.prescriptionsInBatch?.length || 0) - 1;
    }

    get isBatchMode(): boolean {
        return !!this.prescriptionsInBatch?.length;
    }

    get currentBatchItemIsExistingFill(): boolean {
        return this.prescriptionsInBatch?.[this.batchIndex]?.isExistingFill === true;
    }

    created() {
        this.create();
    }

    // noinspection JSUnusedGlobalSymbols
    mounted() {
        this.mount();
        this.fetchDispenseErrorLabel();
        this.loadLocalPrinters();

        if (this.$route.query?.scrollTo === 'billing') {
            this.pendingScrollToBilling = true;
        }

        document.addEventListener("keydown", this.preventDefaultForHotkeys);
        document.addEventListener("keyup", this.hotKeysListener);
    }

    // noinspection JSUnusedGlobalSymbols
    beforeDestroy() {
        // Cancel any pending escript request when component is destroyed
        this.fetchEscriptCancelTokenSource?.cancel("Component destroyed");

        document.removeEventListener("keydown", this.preventDefaultForHotkeys);
        document.removeEventListener("keyup", this.hotKeysListener);
    }

    @Watch('$route')
    async onRouteChange(newRoute: any, oldRoute: any) {
        if (newRoute.name !== oldRoute.name) return;

        const getRouteRxId = (route: any) => {
            const s = Number(route.params.storeID);
            const r = Number(route.params.rxNumber);
            const f = Number(route.params.rfNumber);
            if (!isNaN(s) && !isNaN(r) && !isNaN(f)) return `${s}-${r}-${f}`;
            return route.params.rxId || null;
        };

        const oldRxId = getRouteRxId(oldRoute);
        const newRxId = getRouteRxId(newRoute);

        if (oldRxId !== newRxId && newRxId) {
            this.clearForm();
            this.create();
            this.mount();
            if (newRoute.query?.scrollTo === 'billing') {
                this.pendingScrollToBilling = true;
            }
        }
    }

    // Watch for changes to route props (query parameters passed as props)
    // Each prop has its own watcher to ensure they all trigger correctly
    @Watch('queueItemID')
    onQueueItemIDChange(newVal: string | undefined, oldVal: string | undefined) {
        this.handleQueryPropsChange(newVal, oldVal);
    }

    @Watch('imageID')
    onImageIDChange(newVal: string | undefined, oldVal: string | undefined) {
        this.handleQueryPropsChange(newVal, oldVal);
    }

    @Watch('escriptID')
    onEscriptIDChange(newVal: string | undefined, oldVal: string | undefined) {
        this.handleQueryPropsChange(newVal, oldVal);
    }

    @Watch('programTransfer')
    onProgramTransferChange(newVal: string | undefined, oldVal: string | undefined) {
        this.handleQueryPropsChange(newVal, oldVal);
    }

    async handleQueryPropsChange(newVal: string | undefined, oldVal: string | undefined) {
        console.debug("query props change", oldVal, newVal);

        // Skip if this is the initial component mount (both old and new are undefined)
        // But allow transition from undefined -> value (e.g., auto mode navigation from blank page)
        if (oldVal === undefined && newVal === undefined) {
            return;
        }

        // Skip if going from value -> undefined (clearing/navigating away)
        if (oldVal !== undefined && newVal === undefined) {
            return;
        }

        // Only reinitialize if we have at least one query prop set
        const hasNewPrescription = this.queueItemID || this.imageID || this.escriptID || this.programTransfer;
        if (!hasNewPrescription) {
            return;
        }

        console.log('Route query props changed, reloading prescription data');

        // Clear the form to reset all prescription data (including images)
        // This prevents stale data from previous prescription from sticking around
        this.clearForm(false, false);

        // Release all locks from previous prescription before loading new one
        await this.clearAllLocks();
        this.prescriptionLock = null;

        // Reinitialize with new data
        this.create();
        this.mount();
    }

    preventDefaultForHotkeys(e: KeyboardEvent) {
        if (e.code === "F5") e.preventDefault();
        if (e.altKey && e.key === "b") e.preventDefault();
    }

    hotKeysListener(e: KeyboardEvent) {
        if (e.code === "F5" && this.patient?.id) this.openRxHistory();
        if (e.altKey && e.key === "b") this.insuranceComponent.focus();
    }

    create() {
        console.debug("starting prescription details create");
        // Props are now passed from router, already available as this.queueItemID, etc.
        // They're strings from query params, converted to numbers where needed
        this.fromIngestion = !!this.queueItemID;
        if (this.queueItemID) {
            this.savedIngestionQueueItemID = this.queueItemID;
        }
        if (this.escriptID) {
            this.savedEscriptID = this.escriptID;
        }
        if (this.programTransfer) {
            this.savedProgramTransfer = this.programTransfer;
        }

        if (this.fromIngestion) {
            this.buttonTitles.clear = "";
            this.buttonTitles.cancel = "Back to Ingestion";
            this.buttonTitles.new = "Save and New";
            this.buttonTitles.save = "Save and Finish";
            this.buttonTitles.saveAndStay = "Save";
        } else if (!this.routeRxId && !this.routeDispenseErrorId) {
            // Show "Save and New" for all new prescriptions, not just ingestion queue
            this.buttonTitles.new = "Save and New";
        }

        if (this.routeRxId || this.routeDispenseErrorId) {
            this.getPrescription(this.routeRxId);
            this.buttonTitles = {
                save: "Save Changes",
                clear: "",
                new: "Save and New",
                cancel: "",
            };

            if (this.routeDispenseErrorId) {
                this.buttonTitles.cancel = "Back to " + this.dispenseErrorLabel + " List";
            } else if (this.routeRxId) this.buttonTitles.cancel = "Back";
        }

        if (this.isBatchMode) {
            this.buttonTitles.clear = "";
            this.buttonTitles.cancel = "Next";
            this.buttonTitles.new = "";
            this.buttonTitles.save = "Save and Next";

            if (this.prescriptionsInBatch?.length) this.nextRx();
        }
    }

    mount(fetchQueueItem = true) {
        console.debug("starting prescription details mount");
        Axios.get<Record<string, boolean>>("/api/Configuration/HideDispensingFields,disableFutureWrittenDates,EnableEquivalentDrugCheck,EnforcePackageSizeBilling")
            .then(resp => {
                this.showDispensingFields = !resp.data.HideDispensingFields;
                this.disableFutureWrittenDates = resp.data.disableFutureWrittenDates;
                this.enableEquivalentDrugCheck = resp.data.EnableEquivalentDrugCheck;
                this.enforcePackageSizeBilling = resp.data.EnforcePackageSizeBilling;
            })
            .catch(err => console.error('Error fetching configuration:', err));
        this.getSubStatusConfiguration();
        this.getPrescriberCustomContactTemplates();
        if (this.imageID) {
            console.log("create with imageID");
            this.createPrescription(Number(this.imageID));
        }
        if (this.escriptID) {
            console.log("create with escriptID");
            this.savedEscriptID = this.escriptID;
            this.createPrescriptionEscript(Number(this.escriptID));
        }
        if (this.programTransfer) {
            console.log("create with programTransfer");
            this.savedProgramTransfer = this.programTransfer;
            this.createPrescriptionFromTransfer(Number(this.programTransfer));
        }
        if (this.queueItemID) {
            this.savedIngestionQueueItemID = this.queueItemID;
            this.addLockWithURL(`/IngestionQueue/${this.queueItemID}/Lock`, 60000);

            if (fetchQueueItem) {
                Axios.get<QueueItem>(`/IngestionQueue/${this.queueItemID}`)
                    .then((res) => {
                        if (res.data.id) this.queueItem = res.data;
                        if (this.queueItem?.rxNumber) {
                            const rxId = `${this.queueItem.storeID}-${this.queueItem.rxNumber}-${this.queueItem.rfNumber}`;
                            this.getPrescription(rxId);
                        }
                    })
                    .catch((err) => {
                        const error = err as AxiosError;
                        if (error.response?.status === 418) {
                            this.showLockedMessage(
                                EntityType.IngestionQueueItem,
                                this.queueItemID!.toString(),
                                `Queue Item ${this.queueItemID}`,
                                error.response.data
                            );
                        } else {
                            console.error("Error while loading item queue data.", {
                                err,
                                response: error.response,
                            });
                        }
                    });
            }
        }
    }

    handlePatientDocSaved() {
        if (this.popQueue()) return;
        this.backToPreviousPage(true);
    }

    backToPreviousPage(skipBillingValidation = false) {
        if (this.fromIngestion) {
            if (this.validBillingInformation || skipBillingValidation) {
                this.$router.push({ name: "Ingestion" });
            } else {
                this.$notification(
                    NotificationOptions.notificationOptionsPreset(
                        "Complete billing information before going back to ingestion list.",
                        NotificationOptions.NotificationTypes.danger,
                    ),
                );
            }
        } else if (this.routeDispenseErrorId) {
            this.$router.push({ name: "DispenseErrors" });
        } else if (previousRoute) {
            this.$router.push(previousRoute);
        } else {
            this.$router.go(-1);
        }
    }

    scrollToBillingSection() {
        this.$nextTick(() => {
            const el = this.$refs.insuranceBillingSection as HTMLElement;
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
    }

    claimsLoaded(billedClaims: Claim[], rx: Prescription) {
        this.claims = billedClaims;
        // Assign claims to prescription for transfer validation
        this.prescription.claims = billedClaims;
        this.isClaimsLoading = false;
        this.updatePricing(rx);

        if (this.pendingScrollToBilling) {
            this.pendingScrollToBilling = false;
            this.scrollToBillingSection();
        }
    }

    onCompoundPresetChanged(presetId: number | null) {
        if (this.prescription) {
            this.prescription.compoundPresetID = presetId;
        }
    }

    updatePricing(rx: Prescription) {
        if (rx?.rxNumber) {
            this.prescription.patientPay = rx.patientPay;
            this.prescription.cost = rx.cost;
            this.prescription.totalPrice = rx.totalPrice;
            this.prescription.tax = rx.tax;
            this.prescription.priceScheduleID = rx.priceScheduleID;
            this.prescription.status = rx.status;
            this.statusCode = rx.status;
        }
    }

    changeDispense(num: number) {
        if (this.prescription.quantity.toString().length > 11) {
            this.prescription.quantity = Number(
                this.prescription.quantity.toString().substring(0, 11),
            );
        }
        if (num.toString().length > 11) {
            num = Number(num.toString().substring(0, 11));
        }
        this.dispensedQuantity = Number(num);
        this.prescription.dispensed = Number(num);
    }

    handleDaysSupplyCalculated(calculatedDaysSupply: number) {
        // Only update days supply if it's empty or zero
        if (!this.prescription.daySupply || this.prescription.daySupply === 0) {
            this.prescription.daySupply = calculatedDaysSupply;
        }
    }

    fetchPrescription(prescription: Prescription) {
        if (prescription.rfNumber || prescription.rfNumber == 0) {
            this.getPrescription(prescription.rxID);
        }
    }

    toggleUrgent() {
        const newUrgent = !this.prescription.isUrgent;
        const rxId = `${this.prescription.storeID}-${this.prescription.rxNumber}-${this.prescription.rfNumber}`;
        Axios.post(`/Prescription/${rxId}/urgent`, newUrgent, { headers: { 'Content-Type': 'application/json' } })
            .then(() => {
                this.prescription.isUrgent = newUrgent;
            })
            .catch(() => {
                this.$notification(NotificationOptions.notificationOptionsPreset("Failed to update urgent status.", NotificationOptions.NotificationType.ERROR));
            });
    }

    rxTransferRefresh(transferredPrescription: Prescription) {
        this.getPrescription(transferredPrescription.rxID).then(
            this.replaceRouterParamsWithCurrentPrescription,
        );
    }

    // noinspection SpellCheckingInspection
    /**
     * rxID: Concatenated Rx Ids in the format StoreID-RxNumber-RfNumber
     * setupRefill: If true, the method will load the prescription for RxID and immediately will set up its refill in the front end ready to be edited and saved.
     */
    getPrescription(rxID: string, setupRefill = false) {
        return new Promise((resolve, reject) => {
            this.addLockWithURL(`/Prescription/${rxID}/Lock`, 60000)
                .then((result) => {
                    this.prescriptionLock = result;
                })
                .catch((error) => {
                    if (error.response && error.response.status == 418) {
                        this.showLockedMessage(
                            EntityType.Prescription,
                            rxID,
                            rxID,
                            error.response.data,
                        );
                    }
                })
                .finally(() => {
                    // linked objects will be automatically fetched by the SearchComponent when we set the objects...
                    // if we manually fetch them, they are called for twice.
                    Axios.get(`api/Prescription/${rxID}`)
                        .then((response) => {
                            if (!this.prescription) return reject();

                            this.prescription = new Prescription(response.data);
                            this.setViewValues();

                            this.initialDispensedQty = this.prescription.dispensed;

                            this.patient.id = this.prescription.patientID;
                            this.doctor.id = this.prescription.prescriberID;
                            this.store.id = this.prescription.storeID;
                            this.program.id = this.prescription.programID || 0;
                            this.originalFillDate = this.prescription.fillDate;

                            this.enableExpirationDateEstimation = false;

                            switch (this.prescription.drugSource) {
                                case DrugSource.GsddFederal:
                                    this.drug = new Drug(this.prescription.packageID);
                                    break;
                                case DrugSource.NonGsddProduct:
                                    this.drug = new NonGSDDDrug(this.prescription.packageID);
                                    break;
                                case DrugSource.CompoundProduct:
                                    this.drug = new CompoundDrug(this.prescription.packageID);
                                    break;
                            }
                            this.getDrugDetails(this.drug);

                            //Getting the information about the last fill
                            this.$http
                                .get<Prescription>(
                                    `/Prescription/last-fill/${this.prescription.patientID}/${this.prescription.storeID}/${this.prescription.rxNumber}`,
                                )
                                .then((res) => {
                                    if (res.data?.rxNumber > 0) {
                                        this.lastFill = new Prescription(res.data);
                                    }
                                })
                                .catch((err) => {
                                    console.error("Error while getting last fill info", {
                                        err,
                                        response: err?.response,
                                    });
                                    this.$notification(NotificationOptions.error(err));
                                });

                            if (this.prescription.originPharmacyId) {
                                this.$http.get<PharmacyRecord>(`/api/StoredPharmacy/${this.prescription.originPharmacyId}`)
                                    .then(pharmRecord => {
                                        this.originPharmacy = new PharmacyRecord(pharmRecord.data);
                                    }).catch(err => this.$notification(NotificationOptions.error(err)));
                            }

                            //Setting up RX form in a refill batch
                            if (setupRefill) {
                                this.fetchEscript();
                                if (!this.currentBatchItemIsExistingFill) {
                                    this.newRefill();
                                }

                                // Apply MedSync short fill adjustments if provided
                                if (this.medSyncShortFillData?.isMedSyncShortFill) {
                                    this.prescription.daySupply = this.medSyncShortFillData.adjustedDaysSupply;
                                    this.prescription.dispensed = this.medSyncShortFillData.adjustedDispensed;
                                    this.dispensedQuantity = this.medSyncShortFillData.adjustedDispensed;
                                }

                                this.$http
                                    .get<AutoRefillSubscription>(
                                        `autorefill/subcriptions-by-rx/${this.prescription.storeID}/${this.prescription.rxNumber}`,
                                    )
                                    .then((res) => {
                                        if (res.data.id) {
                                            this.autoRefillSubscription =
                                                new AutoRefillSubscription(res.data);
                                        }
                                    })
                                    .catch((err) => {
                                        console.error("Error while getting autorefill sub data.", {
                                            err,
                                            response: err?.response,
                                        });
                                        this.$notification(NotificationOptions.error(err));
                                    });
                            } else {
                                this.fetchDispenseError();
                                this.fetchEscript();
                            }

                            resolve(undefined);
                        })
                        .catch((error) => {
                            console.error("Error while getting Prescription", {
                                error,
                                response: error?.response,
                            });
                            reject();
                        });
                });
        });
    }

    async fetchEscript() {
        this.fetchEscriptCancelTokenSource?.cancel("New escript fetch started");

        let rxId = this.prescription.rxID;
        if (rxId.endsWith("-")) rxId += "0";
        const capturedRxId = rxId;

        this.fetchEscriptCancelTokenSource = Axios.CancelToken.source();

        try {
            const resp = await Axios.get(`/escript/prescription/${rxId}`, {
                cancelToken: this.fetchEscriptCancelTokenSource.token
            });

            if (resp && resp.data?.eScript) {
                // Validate that the response is still relevant to the current prescription.
                // Prefer matching by escriptID (definitive); fall back to normalized rxID so
                // we don't over-reject when this.prescription was swapped mid-load.
                const currentRxId = this.prescription.rxID;
                const normalizedCurrentRxId = currentRxId.endsWith("-") ? currentRxId + "0" : currentRxId;
                const incomingEscriptId = resp.data.eScript.id;
                const prescriptionEscriptId = this.prescription.escriptID;

                const matchesByEscriptId =
                    prescriptionEscriptId != null && incomingEscriptId === prescriptionEscriptId;
                const matchesByRxId = normalizedCurrentRxId === capturedRxId;

                if (!matchesByEscriptId && !matchesByRxId) {
                    console.warn("Escript response ignored - prescription changed during async request", {
                        requestedRxId: capturedRxId,
                        currentRxId: normalizedCurrentRxId,
                        incomingEscriptId,
                        prescriptionEscriptId,
                    });
                    return;
                }
                this.escript = new Escript(resp.data.eScript);

                // Do not overwrite the prescription's escriptID with a CancelRx escript — that
                // would break pairing logic which relies on escriptID pointing to the originating
                // NewRx/RxRenewalResponse/RxChangeResponse escript. Track the CancelRx escript
                // separately so the Prescription Details page can display its watermarked image
                // as the primary image without breaking pairing.
                if (!resp.data.message?.cancel) {
                    this.prescription.escriptID = resp.data.eScript.id;
                    this.savedEscriptID = resp.data.eScript.id;
                } else {
                    this.cancelRxEscriptID = resp.data.eScript.id;
                }
            }
        } catch (error) {
            if (Axios.isCancel(error)) {
                console.log("Escript request cancelled:", error.message);
                return;
            }
            console.error("Error while getting escript", {
                error,
                response: (error as any)?.response,
            });
        }
    }

    fetchDispenseError() {
        Axios.get(`DispenseError/By-RxId/${this.prescription.rxID}`)
            .then((response) => {
                const findCreatedCallBack = (err: DispenseError) =>
                    err.statusEnum == DispenseError.DispenseErrorStatusEnum.Created;
                const emptyDispenseError = new DispenseError(this.prescription);
                this.dispenseErrors = response.data.map((err: DispenseError) =>
                    Object.assign(new DispenseError(), err),
                );
                if (this.dispenseErrors?.some(findCreatedCallBack)) {
                    this.dispenseError =
                        this.dispenseErrors.find(findCreatedCallBack) || emptyDispenseError;
                } else {
                    this.dispenseError = emptyDispenseError;
                }
            })
            .catch((error) => {
                console.error("Error while getting error", {
                    error,
                    response: error?.response,
                });
            });
    }

    dropzoneSending(_file: any, xhr: XMLHttpRequest, _formData: FormData) {
        xhr.setRequestHeader("Authorization", Axios.defaults.headers["Authorization"]?.toString() ?? "");
    }

    dropzoneUploadSuccess(file: any, response: any) {
        const dropzone: any = this.$refs.myVueDropzone;
        // noinspection JSUnresolvedReference
        dropzone.removeFile(file);

        // reload prescription's image
        this.prescription.imageID = response;
    }

    @Watch("searchProgramGetParams") searchProgramParamsChanged() {
        this.programSearchComponent?.reloadResults();
    }

    @Watch("writtenDate") changedWrittenDate(value: string | Date | null) {
        let val = value;
        if (!(val instanceof Date)) val = moment(val).toDate();

        this.prescription.writtenDate = val;

        if (value && this.enableExpirationDateEstimation) {
            this.estimateExpirationBasedOnPackage(this.prescription.packageID, this.prescription.drugSource, val);
        }

        if (!this.enableExpirationDateEstimation) this.enableExpirationDateEstimation = true;
    }

    @Watch("prescription.imageID")
    async fetchPrescriptionImagePages(imageID: number | null | undefined) {
        // Reset page count when imageID becomes null/undefined
        if (!imageID) {
            this.prescriptionImagePages = 0;
            return;
        }

        // Fetch image information to get the page count and set origin if from fax
        try {
            const response = await Axios.get(`image/ImageInformation/${imageID}`);
            this.prescriptionImagePages = response.data?.pageCount ?? 0;

            // If the prescription image was received via fax, set the Origin to Fax
            if (response.data?.createdBy === "system/SRFax") {
                this.originCode = OriginCode.Fax;
            }
        } catch (error) {
            // If fetching fails, reset to 0
            this.prescriptionImagePages = 0;
        }
    }

    estimateExpirationBasedOnPackage(packageId: number, source: DrugSource, writtenDate: Date) {
        const addressState = this.store.addressState;
        let estimatedExpirationDate = moment(writtenDate);
        if (packageId && addressState) {
            Axios.get(`Drug/${packageId}/${source}/${addressState}/ScheduledExpiration`).then((response) => {
                const deaSchedule = Number(response.data);
                this.expiration = estimatedExpirationDate.add(deaSchedule, 'days').toDate();
            });
        } else {
            estimatedExpirationDate = estimatedExpirationDate.add(365, "days");
            this.expiration = estimatedExpirationDate.toDate();
        }
    }

    formattedDateAndTime(value: Date): string {
        if (!value) return "";
        const date = moment.utc(value).local();
        return date.format("lll");
    }

    @Watch("store") storeUpdate(value: any) {
        if (!value || !this.prescription) return;
        this.prescription.storeID = value.id;
    }

    patientUpdate(value: any) {
        if (!value || !this.prescription) return;

        this.prescription.patientID = value.id;
        if (
            !this.prescription.deliveryOption &&
            (value.defaultDelivery != undefined || value.defaultDelivery != null) &&
            !this.prescription.rxNumber
        ) {
            this.deliveryCode = value.defaultDelivery;
        }
    }

    @Watch('patient.id')
    async fetchPatientPrescribers() {
        const patientId = this.patient?.id;
        if (!patientId) {
            this.patientPrescribers = [];
            return;
        }
        try {
            const response = await Axios.get(`/api/Prescriber/by-patient/${patientId}`);
            this.patientPrescribers = response.data || [];
        } catch {
            this.patientPrescribers = [];
        }
    }

    prescriberResultsModifier(searchResults: any[]) {
        if (!this.patientPrescribers.length) return searchResults;

        const patientPrescriberIds = new Set(this.patientPrescribers.map((p: any) => p.id));
        const filtered = searchResults.filter((r: any) => !patientPrescriberIds.has(r.id));

        return [
            { heading: true, label: "Patient's Prescribers" },
            ...this.patientPrescribers,
            { heading: true, label: "Search Results", visible: !!filtered.length },
            ...filtered,
        ].filter((item: any) => item.heading ? item.visible !== false : true);
    }

    doctorUpdate(value: any) {
        if (!value || !this.prescription) return;

        this.prescription.prescriberID = value.id;
        this.setRecommendedProgram();
    }

    autoselectProgram(programs: Program[]) {
        // Do not autoselect if a program is already set
        if (this.program?.id) return null;
        // Do not autoselect for escript or transfer flows (handled by forceProgramID)
        if (this.escriptID || this.savedEscriptID) return null;
        if (this.programTransfer || this.savedProgramTransfer) return null;
        // Autoselect if only one program is available
        if (programs.length === 1) return programs[0];
        return null;
    }

    programUpdate(value: any) {
        if (!this.prescription) return;
        if (!value?.id) this.program = new Program();

        this.prescription.programID = value?.id;
    }

    @Watch("drugDetails")
    drugDetailsUpdate(value: Drug | null, _oldValue: Drug | null) {
        this.imageURLs = [];
        const baseURL = Axios?.defaults?.baseURL || "";
        if (!value) return;
        const imageURI =
            baseURL + `/drug/get-image?packageID=${value.drugId}&drugSource=${value.source}`;
        this.imageURLs.push(imageURI);

        this.drug.ndc = value.ndc;
        if (this.drug instanceof Drug) this.drug.productNameLong = value.productNameLong;
    }

    @Watch("drugDetails")
    @Watch("prescription.dispensed")
    drugOrDispenseUpdate() {
        const cost = this.drugDetails?.vendors?.find((v: any) => v.displayOrder == 1)?.cost ?? 0;
        if (cost) {
            this.prescription.estimatedCost = this.prescription.dispensed * cost;
        }
    }

    private isPackageSizeMultipleInvalid(): boolean {
        if (!this.enforcePackageSizeBilling) return false;
        if (this.statusCode === RefillStatusCode.Void
            || this.statusCode === RefillStatusCode.OnHold
            || this.statusCode === RefillStatusCode.TransferOut
            || this.substatusCode === RxDeniedSubStatusId) return false;
        if (!this.drugDetails) return false;
        const billingUnit = this.drugDetails.billingUnit;
        const packageSize = this.drugDetails.packageSize;
        if (billingUnit?.toLowerCase() === "ea" || !packageSize || packageSize <= 0) return false;
        const dispensed = this.dispensedQuantity;
        if (!dispensed || dispensed <= 0) return false;
        const ratio = dispensed / packageSize;
        return Math.abs(ratio - Math.round(ratio)) > 0.0001;
    }

    @Watch("drug")
    drugUpdate(value: DrugProduct, oldValue: DrugProduct) {
        if (
            !value ||
            !value.drugId ||
            (oldValue && oldValue.drugId && +value.drugId == +oldValue.drugId && (value as Drug).productID == (oldValue as Drug).productID)
            //|| this.disableFields
        ) {
            return;
        }

        let packId: number = +value.drugId;
        if ((value as Drug).productID) this.fetchDrugCategory((value as any).productID);

        let drug: DrugProduct;
        switch (value.source) {
            case DrugSource.GsddFederal:
                drug = new Drug(packId, value);
                break;
            case DrugSource.NonGsddProduct:
                drug = new NonGSDDDrug(packId, value);
                break;
            case DrugSource.CompoundProduct:
                drug = new CompoundDrug(packId, value);
                break;
        }

        this.fetchDrug(drug);
        this.fetchDrugAlert(drug);
    }

    get showDeaAlert() {
        const needsDea = this.isScheduleClassDrug;
        const hasDea = !!this.doctor?.deaNumber;
        return needsDea && !hasDea;
    }

    getDrugDetails(drug: DrugProduct) {
        const drugId = drug.drugId;
        this.loadingDrugDetails = true;
        Axios.get<DrugProduct>(`/Drug/${drugId}`, {
            params: {
                getProgram: true,
                getVendors: true,
                drugSource: drug.source,
            },
        })
            .then((response) => {
                if (this.prescription?.packageID == response.data.drugId) {
                    switch (response.data.source) {
                        case DrugSource.GsddFederal:
                            this.drug = new Drug(response.data.drugId, response.data);
                            break;
                        case DrugSource.NonGsddProduct:
                            this.drug = new NonGSDDDrug(response.data.drugId, response.data);
                            break;
                        case DrugSource.CompoundProduct:
                            this.drug = new CompoundDrug(response.data.drugId, response.data);
                    }

                    this.drugDetails = response.data;
                    this.drugDetails.status = this.drug.activeStatus;
                    this.prescription.drugSource = drug.source;

                    Axios.get<any[]>(`/Drug/${drugId}/Inventory`, {
                        params: {
                            drugSource: drug.source,
                            fillingLocationsOnly: true,
                        },
                    })
                        .then((invResp) => {
                            let storeInventories = invResp.data.filter(
                                (inv) => inv.storeId == this.prescription.storeID,
                            );
                            if (storeInventories.length == 1) {
                                this.drugInventory = storeInventories[0];
                            } else {
                                this.drugInventory = storeInventories.filter(
                                    (inv) => inv.programId == this.prescription.programID,
                                )[0];
                            }
                        })
                        .catch((err) => this.$notification(NotificationOptions.error(err)));
                } else {
                    console.warn(
                        "drug details didn't match the prescription's packageID",
                        response.data,
                    );
                }
            })
            .catch((error) => {
                console.warn(error);
            })
            .finally(() => {
                this.loadingDrugDetails = false;
            });
    }

    fetchDrugAlert(drug: DrugProduct) {
        Axios.get(`/Drug/${drug.drugId}/Alert`, {
            params: {
                getProgram: true,
                getVendors: true,
                drugSource: drug.source,
            },
        })
            .then((response) => {
                this.drugAlert = response.data;
            })
            .catch((error) => {
                console.error("Error while getting drug alert", {
                    error,
                    response: error?.response,
                });
            });
    }

    fetchDrug(drug: DrugProduct) {
        const packId = Number(drug.drugId);
        if (this.prescription) {
            this.prescription.packageID = packId;
            this.prescription.drugSource = drug.source;
        }
        //Estimate expiration date only if we are creating a new RX
        if (!this.prescription?.rxNumber) {
            this.estimateExpirationBasedOnPackage(packId, drug.source, moment(this.writtenDate).toDate());
        }

        this.loadingDrugDetails = true;
        this.errorLoadingDrugDetails = false;
        Axios.get<DrugProduct>(`/Drug/${drug.drugId}`, {
            params: {
                getProgram: true,
                getVendors: true,
                drugSource: drug.source
            },
        })
            .then((response) => {
                if (!this.disableFields && this.prescription?.packageID == response.data.drugId) {
                    this.drugDetails = response.data;
                    const deaClass = this.drugDetails.deaClass ? this.drugDetails.deaClass : this.drugDetails.deaClassificationId;
                    this.isScheduleClassDrug = deaClass < 5;
                    this.setRecommendedProgram();
                }
            })
            .catch((error) => {
                console.error("Error while getting drug details", {
                    error,
                    response: error?.response,
                });
                this.errorLoadingDrugDetails = true;
            })
            .finally(() => {
                this.loadingDrugDetails = false;
            });
    }

    setRecommendedProgram() {
        // Do not attempt to set the recommended program if we've already saved the prescription before
        if (this.prescription?.rxNumber) return;

        // Do not attempt to set the recommended program if we've already set it from the transfer
        if (this.programSetFromTransfer) return;

        const programPrescriberId = this.doctor?.programId || 0;
        const drugProgramId = this.drugDetails?.program?.id || 0;

        //Prescriber program takes precedence over the drug program
        const programId = programPrescriberId || drugProgramId;

        // Do not clear an existing program selection if there is no recommendation
        if (!programId) return;

        if (!this.program) this.program = new Program();

        this.program.id = programId;
    }

    get canCreateRefill() {
        if (!this.prescription) return false;
        if (this.prescription.status == RefillStatusCode.TransferOut) return false;
        if (this.prescription.isExpired) return false;
        if (this.prescription.status == RefillStatusCode.OnHold && this.prescription.subStatus == RxDeniedSubStatusId) return false;
        if (this.prescription.status == RefillStatusCode.Discontinued) return false;
        if (!this.prescription.rfNumber && this.prescription.status == RefillStatusCode.Void) return false;
        return true;
    }

    /**
     * Prepares the form to submit a new refill for the current prescription.
     */
    newRefill() {
        // Check if the patient is deceased
        if (this.patient?.deceased) {
            this.$notification(NotificationOptions.error('Cannot create refills for deceased patients.'));
            return;
        }

        if (!this.canCreateRefill) return;

        const nextRfNumber = (this.prescription.rfNumber || 0) + 1;
        this.nextRefillRxId = Prescription.createRxId(
            this.prescription.storeID,
            this.prescription.rxNumber,
            nextRfNumber,
        );
        this.newRefillMode = true;

        this.prescription.rfNumber = undefined;
        this.prescription.priceScheduleID = undefined;
        this.prescription.fillDate = moment().startOf("day").toDate();
        this.prescription.lotNumber = "";
        this.prescription.serialNumber = "";

        this.prescription.drugsFilledDate = null;
        this.prescription.drugExpirationDate = null;
        this.prescription.cost = null;
        this.prescription.patientPay = null;
        this.prescription.totalPrice = null;
        this.prescription.tax = null;

        const comp = this.$refs.insuranceComponent as InsuranceBillingComponent;
        if (comp) comp.fetchPatientClaims();
    }

    unVoid() {
        this.$bvModal
            .msgBoxConfirm(
                "Prescription status will be set back to Refillable and save changes if you confirm. Are you sure you want to unVoid this prescriptions?",
                {
                    title: "Confirm",
                    size: "sm",
                    buttonSize: "sm",
                    okVariant: "danger",
                    okTitle: "YES",
                    cancelTitle: "NO",
                    footerClass: "p-2",
                    hideHeaderClose: false,
                    centered: true,
                },
            )
            .then((value) => {
                if (!value) return;
                this.statusCode = RefillStatusCode.Refillable;
                this.save();
            })
            .catch((err) => {
                console.error("Error caught on unVoid button.", err);
            });
    }

    cancelPV2() {
        if (this.prescription.hasBeenShipped) {
            this.$notification(
                NotificationOptions.notificationOptionsPreset(
                    "Cannot cancel PV2 on shipped prescription",
                    NotificationOptions.NotificationTypes.danger,
                ),
            );
            return;
        }

        this.$bvModal
            .msgBoxConfirm(
                "By Cancelling PV2, any previous PV2 done for this RX will cancelled and inventory will be put back to stock. Are you sure?",
                {
                    title: "Confirm",
                    size: "sm",
                    buttonSize: "sm",
                    okVariant: "danger",
                    okTitle: "YES",
                    cancelTitle: "NO",
                    footerClass: "p-2",
                    hideHeaderClose: false,
                    centered: true,
                },
            )
            .then((value) => {
                if (!value) return;

                const rxId = this.prescription.rxID;
                if (!rxId) return;

                Axios.get(`/Prescription/${rxId}/cancel-pv2/`)
                    .then((response) => {
                        if (response.data.cancelled) {
                            this.prescription.pV2ID = null;
                            this.$notification(
                                NotificationOptions.notificationOptionsPreset(
                                    "PV2 was cancelled.",
                                    NotificationOptions.NotificationTypes.success,
                                ),
                            );
                        } else {
                            this.$notification(
                                NotificationOptions.errorSaveNotificationPreset("PV2 cancellation"),
                            );
                        }
                    })
                    .catch((error) => {
                        console.error("Error while cancelling PV2", {
                            error,
                            response: error?.response,
                        });
                        this.$notification(
                            NotificationOptions.errorSaveNotificationPreset(
                                "PV2 cancellation",
                                error,
                            ),
                        );
                    });
            })
            .catch((err) => {
                console.error("Error caught on cancel PV2 button.", err);
            });
    }

    newPrescription() {
        // Check if the patient is deceased
        if (this.patient?.deceased) {
            this.$notification(
                NotificationOptions.error('Cannot create new prescriptions for deceased patients.')
            );
            return;
        }

        this.newMode = true;
        this.createPrescription();
    }

    createPrescription(imageID?: number) {
        this.prescription = new Prescription();
        this.setPrescriptionDefaultValues();

        // Setting imageID will trigger the @Watch decorator which fetches image information
        this.prescription.imageID = imageID;
    }

    setPrescriptionDefaultValues() {
        //Default values need to be assigned to another variable
        this.prescription.originalRxDate = moment().utc(true).toDate();
        this.prescription.fillDate = moment().utc(true).toDate();

        this.dawCode = DAWCode.NotSpecified;
        this.originCode = OriginCode.NotSpecified;
        this.statusCode = RefillStatusCode.Refillable;
        this.substatusCode = null;
        this.writtenDate = moment().utc(true).toDate();
    }

    createPrescriptionFromTransfer(transferId?: number) {
        if (!transferId) return;
        Axios.get(`ProgramTransfer/${transferId}`).then((resp) => {
            this.escriptResponseMessage = resp.data;
            // console.log("programTransfer resp: ", resp);
            this.prescription = new Prescription(resp.data.prescription);
            this.prescription.originalRxDate = moment().utc(true).toDate();
            const utcTransferDate = moment.utc(resp.data.prescription.writtenDate);
            this.writtenDate = new Date(utcTransferDate.year(), utcTransferDate.month(), utcTransferDate.date());

            this.prescription.fillDate = moment().utc(true).toDate();
            this.prescription.dispensed = this.prescription.quantity;
            this.dispensedQuantity = this.prescription.dispensed;
            this.originCode = OriginCode.Transfer;
            this.prescription.originCode = OriginCode.Transfer;

            this.drug = new Drug(this.prescription.packageID);
            this.drug.source = this.prescription.drugSource;
            this.getDrugDetails(this.drug);

            if (resp.data.programObj?.id) {
                this.program = new Program(resp.data.programObj);
                this.programSetFromTransfer = true;
            } else {
                this.program = new Program();
                this.setRecommendedProgram();
            }
            this.store = new Store(resp.data.storeObj);
            this.patient = new Patient(resp.data.patientObj);
            this.doctor = new Prescriber(resp.data.prescriberObj);

        });
    }

    createPrescriptionEscript(escriptID?: number) {
        console.log("Creating prescription from escript");

        if (escriptID) {
            this.originCode = OriginCode.Escript;
            Axios.get<EscriptResponse>(`/escript/${escriptID}`)
                .then((resp) => {

                    this.escriptResponseMessage = resp.data.message;
                    this.escript = new Escript(resp.data.eScript);

                    if (resp.data.message?.kind === "NewRx") {
                        this.prescription = new Prescription(resp.data.message.prescription);
                        this.prescription.originalRxDate = moment().utc(true).toDate();
                        this.prescription.fillDate = moment().utc(true).toDate();
                        //Force the written date to the provided day (ignore time/time zone)
                        this.prescription.writtenDate = moment(this.prescription.writtenDate.toString().substring(0, 10)).toDate();
                        this.prescription.escriptID = escriptID;
                        this.prescription.originCode = OriginCode.Escript;
                        this.prescription.daySupply = this.escript.daySupply;
                        this.prescription.directions = resp.data.eScript.smartSig?.sigText ?? resp.data.message?.prescription?.directions;
                        this.prescription.memo = resp.data.eScript.smartSig?.note ?? resp.data.message?.prescription?.memo;
                        this.changeDispense(this.prescription.quantity || 0);
                        this.store = Object.assign(new Store(), resp.data.message.storeObj);

                        this.patient = Object.assign(new Patient(), resp.data.message.patientObj);
                        this.mergeEscriptPatientData(resp.data.message.patient);
                        this.doctor = Object.assign(
                            new Prescriber(),
                            resp.data.message.prescriberObj,
                        );

                        if (resp.data.preferredDrugObj) {
                            if (
                                resp.data.message.drugObj.packageID !=
                                resp.data.preferredDrugObj.packageID
                            ) {
                                this.showDrugChange = true;
                            }
                            this.drug = new Drug(null, resp.data.preferredDrugObj);
                        } else {
                            this.drug = new Drug(null, resp.data.message.drugObj);
                        }

                        this.setViewValues(true);
                    } else if (resp.data.message?.kind === "RxRenewalResponse") {
                        this.prescription = new Prescription(resp.data.message.prescription);
                        this.prescription.originalRxDate = moment().utc(true).toDate();
                        this.prescription.fillDate = moment().utc(true).toDate();
                        //Force the written date to the provided day (ignore time/time zone)
                        this.prescription.writtenDate = moment(this.prescription.writtenDate.toString().substring(0, 10)).toDate();
                        this.prescription.escriptID = escriptID;
                        this.prescription.originCode = OriginCode.Escript;
                        this.prescription.daySupply = this.escript.daySupply;
                        this.prescription.directions = resp.data.eScript.smartSig?.sigText ?? resp.data.message?.prescription?.directions;
                        this.prescription.memo = resp.data.eScript.smartSig?.note ?? resp.data.message?.prescription?.memo;
                        this.changeDispense(this.prescription.quantity || 0);

                        this.store = Object.assign(new Store(), resp.data.message.storeObj);

                        this.patient = Object.assign(new Patient(), resp.data.message.patientObj);
                        this.mergeEscriptPatientData(resp.data.message.patient);
                        this.doctor = Object.assign(
                            new Prescriber(),
                            resp.data.message.prescriberObj,
                        );

                        if (resp.data.preferredDrugObj) {
                            if (
                                resp.data.message.drugObj.packageID !=
                                resp.data.preferredDrugObj.packageID
                            ) {
                                this.showDrugChange = true;
                            }
                            this.drug = new Drug(null, resp.data.preferredDrugObj);
                        } else {
                            this.drug = new Drug(null, resp.data.message.drugObj);
                        }

                        this.setViewValues(true);
                    } else if (resp.data.message?.kind === "RxChangeResponse") {
                        this.prescription = new Prescription(resp.data.message.prescription);
                        this.prescription.originalRxDate = moment().utc(true).toDate();
                        this.prescription.fillDate = moment().utc(true).toDate();
                        //Force the written date to the provided day (ignore time/time zone)
                        this.prescription.writtenDate = moment(this.prescription.writtenDate.toString().substring(0, 10)).toDate();
                        this.prescription.escriptID = escriptID;
                        this.prescription.originCode = OriginCode.Escript;
                        this.prescription.daySupply = this.escript.daySupply;
                        this.prescription.directions = resp.data.eScript.smartSig?.sigText ?? resp.data.message?.prescription?.directions;
                        this.prescription.memo = resp.data.eScript.smartSig?.note ?? resp.data.message?.prescription?.memo;
                        this.changeDispense(this.prescription.quantity || 0);
                        this.store = Object.assign(new Store(), resp.data.message.storeObj);
                        this.patient = Object.assign(new Patient(), resp.data.message.patientObj);
                        this.mergeEscriptPatientData(resp.data.message.patient);
                        this.doctor = Object.assign(
                            new Prescriber(),
                            resp.data.message.prescriberObj,
                        );

                        if (resp.data.preferredDrugObj) {
                            if (
                                resp.data.message.drugObj.packageID !=
                                resp.data.preferredDrugObj.packageID
                            ) {
                                this.showDrugChange = true;
                            }
                            this.drug = new Drug(null, resp.data.preferredDrugObj);
                        } else {
                            this.drug = new Drug(null, resp.data.message.drugObj);
                        }

                        this.setViewValues(true);
                    } else if (resp.data.message?.kind === "RxTransfer") {
                        this.prescription = new Prescription(resp.data.message.prescription);
                        this.prescription.originalRxDate = moment().utc(true).toDate();
                        this.prescription.fillDate = moment().utc(true).toDate();
                        //Force the written date to the provided day (ignore time/time zone)
                        this.prescription.writtenDate = moment(this.prescription.writtenDate.toString().substring(0, 10)).toDate();
                        this.prescription.escriptID = escriptID;
                        this.prescription.originCode = OriginCode.Transfer;
                        this.prescription.daySupply = resp.data.message.drugs.daysSupply;
                        this.prescription.directions = resp.data.eScript.smartSig?.sigText ?? resp.data.message?.prescription?.directions;
                        this.prescription.memo = resp.data.eScript.smartSig?.note ?? resp.data.message?.prescription?.memo;
                        this.changeDispense(this.prescription.quantity || 0);
                        this.store = Object.assign(new Store(), resp.data.message.storeObj);
                        this.originPharmacy = Object.assign(new PharmacyRecord(), resp.data.message.originPharmacyObj);

                        this.patient = Object.assign(new Patient(), resp.data.message.patientObj);
                        this.mergeEscriptPatientData(resp.data.message.patient);
                        this.doctor = Object.assign(
                            new Prescriber(),
                            resp.data.message.prescriberObj,
                        );

                        if (resp.data.preferredDrugObj) {
                            if (
                                resp.data.message.drugObj.packageID !=
                                resp.data.preferredDrugObj.packageID
                            ) {
                                this.showDrugChange = true;
                            }
                            this.drug = new Drug(null, resp.data.preferredDrugObj);
                        } else {
                            this.drug = new Drug(null, resp.data.message.drugObj);
                        }

                        this.setViewValues(true);
                    } else if (resp.data.message?.exception) {
                        const errorMsg = "There was an exception while loading Escript data.";
                        this.$notification(
                            NotificationOptions.notificationOptionsPreset(
                                errorMsg,
                                NotificationOptions.NotificationTypes.danger,
                            ),
                        );
                        console.log(errorMsg, resp.data.message.exception);
                    } else {
                        const errorMsg = "Unknown error, not able to load escript data.";
                        this.$notification(
                            NotificationOptions.notificationOptionsPreset(
                                errorMsg,
                                NotificationOptions.NotificationTypes.danger,
                            ),
                        );
                        console.log(errorMsg, resp);
                    }
                })
                .catch((err) => {
                    const errorMsg = "Error while loading Escript data";
                    this.$notification(
                        NotificationOptions.notificationOptionsPreset(
                            errorMsg,
                            NotificationOptions.NotificationTypes.danger,
                        ),
                    );
                    console.log(errorMsg, {
                        err,
                        response: err.response,
                    });
                });
        }
    }

    /**
     * Merges height and weight from the parsed eScript patient data into the current patient.
     * The parsed patient comes directly from the eScript XML and may have height/weight
     * that the existing DB patient record does not.
     */
    mergeEscriptPatientData(escriptPatient?: Patient) {
        if (!escriptPatient) return;
        if (escriptPatient.height != null) this.patient.height = escriptPatient.height;
        if (escriptPatient.weight != null) this.patient.weight = escriptPatient.weight;
    }

    startEditPatient() {
        this.editPatientForm = true;
    }

    clearForm(continueSameImage = false, clearQueryParams = true) {
        if (this.prescriptionLock && this.prescriptionLock.refreshURL) {
            this.releaseLockAtURL(this.prescriptionLock.refreshURL);
            this.prescriptionLock = null;
        }
        this.clearPrescription(continueSameImage, clearQueryParams);
        this.programSetFromTransfer = false;
        this.drugCategories = [];
        this.drugAlert = "";
        this.claims = [];
        (this.$refs.insuranceComponent as any).clearComponent();
        this.setPrescriptionDefaultValues();

        this.enableExpirationDateEstimation = true;
        this.showDocuStore = false;
        this.initialDispensedQty = 0;

        this.newMode = false;
        this.newRefillMode = false;
        this.$nextTick(() => {
            this.$forceUpdate();
        });
    }

    clearPrescription(continueSameImage = false, clearQueryParams = true) {
        if (this.prescriptionLock && this.prescriptionLock.refreshURL) {
            this.releaseLockAtURL(this.prescriptionLock.refreshURL);
            this.prescriptionLock = null;
        }
        this.prescription = new Prescription();

        this.lastFill = new Prescription();

        if (!continueSameImage) {
            this.patient = new Patient();
            this.doctor = new Prescriber();
            this.store = new Store();
            this.escript = new Escript();
            this.savedEscriptID = undefined;
            this.cancelRxEscriptID = null;
            this.savedProgramTransfer = undefined;
            this.savedIngestionQueueItemID = null;
        }

        this.program = new Program();
        this.drug = new Drug();
        this.expiration = null;
        this.writtenDate = null;
        this.drugDetails = null;
        this.dispenseError = new DispenseError();
        this.dispenseErrors = [];

        this.dispensedQuantity = 0;

        if (clearQueryParams) {
            // when we clear, we need to clear the imageID query if it exists.
            this.clearQuery();
        }
    }

    undoChanges() {
        this.clearForm();
        window.scrollTo(0, 0);
    }

    setViewValues(_isEscript = false) {
        this.settingViewValues = true;
        this.prescription.fillDate = moment(this.prescription.fillDate).toDate();

        this.dawCode = this.prescription.dawCode;
        this.originCode = this.prescription.originCode;
        this.statusCode = this.prescription.status;
        this.substatusCode = this.prescription.subStatus ?? null;
        this.nextTouchDateLocal = this.prescription.nextTouchDate
            ? moment.utc(this.prescription.nextTouchDate).local().format("YYYY-MM-DDTHH:mm")
            : "";
        this.nextTouchDateManualOverride = this.prescription.nextTouchDateManualOverride ?? false;
        this.deliveryCode = this.prescription.deliveryCode;
        this.dispensedQuantity = this.prescription.dispensed;

        if (this.prescription.expirationDate) {
            this.expiration = moment(this.prescription.expirationDate).toDate();
        } else {
            this.expiration = null;
        }
        const utcWritten = moment.utc(this.prescription.writtenDate);
        this.writtenDate = new Date(utcWritten.year(), utcWritten.month(), utcWritten.date());
        this.treatmentTypeCode = this.prescription.treatmentTypeCode ?? null;
        this.settingViewValues = false;
    }

    assignViewValues() {
        this.prescription.dawCode = this.dawCode;
        this.prescription.originCode = this.originCode;
        this.prescription.status = this.statusCode;
        this.prescription.subStatus =
            this.statusCode == RefillStatusCode.OnHold ? this.substatusCode :
                this.statusCode == RefillStatusCode.Discontinued ? this.substatusCode :
                    null;
        this.prescription.deliveryCode = this.deliveryCode;
        this.prescription.treatmentTypeCode = this.treatmentTypeCode;
        this.prescription.nextTouchDate = this.nextTouchDateLocal
            ? moment(this.nextTouchDateLocal).utc().toDate()
            : null;
        this.prescription.nextTouchDateManualOverride = this.nextTouchDateManualOverride;
        this.prescription.expirationDate = this.expiration;
        this.prescription.writtenDate = moment(this.writtenDate).startOf('day').toDate();
    }

    @Watch("statusCode") statusCodeUpdate(newValue: number, oldValue: number) {
        if (
            (oldValue === RefillStatusCode.OnHold ||
                oldValue === RefillStatusCode.AwaitingInsurance) &&
            newValue !== RefillStatusCode.OnHold &&
            newValue !== RefillStatusCode.AwaitingInsurance
        ) {
            this.prescription.fillDate = moment().utc(true).toDate();
        }

        // Clear NextTouchDate when leaving on-hold status
        if (oldValue === RefillStatusCode.OnHold && newValue !== RefillStatusCode.OnHold) {
            this.nextTouchDateLocal = "";
            this.nextTouchDateManualOverride = false;
        }
    }

    @Watch("substatusCode") substatusCodeUpdate() {
        if (this.settingViewValues) return;
        if (this.statusCode !== RefillStatusCode.OnHold || this.nextTouchDateManualOverride) return;

        const selected = this.onHoldSubStatuses.find(ss => ss.value === this.substatusCode);
        if (selected?.offsetValue && selected.offsetValue > 0 && selected.offsetUnit) {
            const unit = selected.offsetUnit.toLowerCase() as moment.unitOfTime.DurationConstructor;
            this.nextTouchDateLocal = moment().add(selected.offsetValue, unit).format("YYYY-MM-DDTHH:mm");
        } else {
            this.nextTouchDateLocal = "";
        }
    }

    openPdfAnnotator() {
        // CancelRx images are display-only: annotating would target the NewRx escript
        // (since prescription.escriptID is preserved for pairing) and silently corrupt it.
        if (this.isCancelRxActive) {
            this.$notification(NotificationOptions.error(
                'CancelRx images are display-only and cannot be annotated.'
            ));
            return;
        }

        if (!this.prescription?.imageID && !this.prescription?.escriptID) {
            this.$notification(NotificationOptions.error("No image available to annotate."));
            return;
        }

        // For new prescriptions (not yet saved), require valid form before annotating
        // because annotation triggers a save to persist the new image ID
        if (!this.prescription.rxNumber && !this.validFormForBilling) {
            this.$notification(NotificationOptions.error(
                `Please complete required fields before adding annotations: ${this.fieldsPending || 'Drug, Patient, Store, etc.'}`
            ));
            return;
        }

        // Reset quick annotate state for manual annotation
        this.quickAnnotateText = null;
        this.isQuickAnnotate = false;

        this.$bvModal.show('prescription-pdf-annotation');
    }

    openQuickAnnotate() {
        if (this.isCancelRxActive) {
            this.$notification(NotificationOptions.error(
                'CancelRx images are display-only and cannot be annotated.'
            ));
            return;
        }

        if (!this.prescription?.imageID && !this.prescription?.escriptID) {
            this.$notification(NotificationOptions.error("No image available to annotate."));
            return;
        }

        if (!this.addNote.trim()) {
            this.$notification(NotificationOptions.error("Please enter a note to add."));
            return;
        }

        // For new prescriptions (not yet saved), require valid form before annotating
        // because annotation triggers a save to persist the new image ID
        if (!this.prescription.rxNumber && !this.validFormForBilling) {
            this.$notification(NotificationOptions.error(
                `Please complete required fields before adding annotations: ${this.fieldsPending || 'Drug, Patient, Store, etc.'}`
            ));
            return;
        }

        // Set up quick annotate with the note text
        this.quickAnnotateText = this.addNote.trim();
        this.isQuickAnnotate = true;

        this.$bvModal.show('prescription-pdf-annotation');
    }

    async onPdfAnnotationSaved(payload: {
        oldImageId: number | null;
        newImageId: number;
        escriptId: number | null;
        saveToAll: boolean;
        orphanUpdates?: Array<{ storeID: number; rxNumber: number; rfNumber: number; newImageId: number }>;
    }) {
        if (!this.prescription) return;

        // Clear the quick annotate state and the note textarea (unless "keep note" is checked)
        if (this.isQuickAnnotate) {
            if (!this.keepHardcopyNote) {
                this.addNote = "";
            }
            this.quickAnnotateText = null;
            this.isQuickAnnotate = false;
        }

        // Handle eScript annotation - just refresh the display, no prescription update needed
        if (payload.escriptId) {
            // Refresh the eScript image display
            if (this.prescriptionImageComponent) {
                (this.prescriptionImageComponent as any).fetchPrescriptionEscriptResponse(payload.escriptId);
            }
            this.$notification(NotificationOptions.success("eScript annotation saved successfully."));
            return;
        }

        // Handle regular image annotation - update ONLY the image ID
        if (payload.newImageId) {
            try {
                if (payload.saveToAll && payload.oldImageId) {
                    await Axios.put("/Prescription/update-sibling-images", {
                        currentImageId: payload.oldImageId,
                        newImageId: payload.newImageId,
                    });

                    const count = this.siblingScriptCount;
                    this.$notification(NotificationOptions.success(
                        `Annotated PDF saved and applied to ${count} prescription${count !== 1 ? 's' : ''}.`
                    ));

                    // Update individually-annotated prescriptions with re-annotated images
                    if (payload.orphanUpdates?.length) {
                        try {
                            for (const update of payload.orphanUpdates) {
                                await Axios.post("/Prescription/updateImage", {
                                    storeID: update.storeID,
                                    rxNumber: update.rxNumber,
                                    rfNumber: update.rfNumber,
                                    newImageId: update.newImageId,
                                });
                            }
                        } catch (orphanErr) {
                            console.error("Failed to update some orphaned siblings:", orphanErr);
                        }
                    }
                } else {
                    // In newRefillMode the prescription row does not yet exist
                    if (!this.newRefillMode) {
                        await Axios.post("/Prescription/updateImage", {
                            storeID: this.prescription.storeID,
                            rxNumber: this.prescription.rxNumber,
                            rfNumber: this.prescription.rfNumber,
                            newImageId: payload.newImageId,
                        });
                    }

                    this.$notification(NotificationOptions.success("Annotated PDF saved and linked to prescription."));
                }

                this.prescription.imageID = payload.newImageId;

                if (this.prescriptionImageComponent) {
                    (this.prescriptionImageComponent as any).fetchPrescriptionImageSource(
                        `image/${payload.newImageId}`
                    );
                }
            } catch (err: any) {
                console.error("Failed to update prescription with new image:", err);
                this.$notification(NotificationOptions.error(err?.response?.data || "Failed to update prescription with annotated image."));
            }
        }
    }

    onPdfAnnotationClosed() {
        // Reset quick annotate state when modal is closed
        this.quickAnnotateText = null;
        this.isQuickAnnotate = false;
    }

    saveAndContinue() {
        this.stayOnPageAfterSave = false;
        this.savePrescription();
    }

    saveAndBill(billInsuranceCallback: Function) {
        if (this.validFormForBilling) {
            this.savePrescription(false, billInsuranceCallback);
        } else {
            billInsuranceCallback(null, true);
            const message =
                "Not possible to save and bill. Please, first fill all required fields." +
                (this.fieldsPending ? " Check the next fields: " + this.fieldsPending : "");
            this.$bvModal.msgBoxOk(message, {
                title: "Not possible to save and bill.",
                size: "sm",
                buttonSize: "sm",
                okVariant: "danger",
                footerClass: "p-2",
                hideHeaderClose: false,
                centered: true,
            });
        }
    }

    saveAndStay() {
        this.stayOnPageAfterSave = true;
        this.saveAndFinish();
    }

    saveAndFinish() {
        if (this.dispenseError?.id) {
            this.$bvModal.show("modal_save_confirmation");
        } else {
            if (this.validFormForBilling) {
                this.checkEquivalentPrescriptions();
            } else {
                const message =
                    "Not possible to save. Please, first fill all required fields." +
                    (this.fieldsPending ? " Check the next fields: " + this.fieldsPending : "");
                this.$bvModal.msgBoxOk(message, {
                    title: "Not possible to save.",
                    size: "sm",
                    buttonSize: "sm",
                    okVariant: "danger",
                    footerClass: "p-2",
                    hideHeaderClose: false,
                    centered: true,
                });
            }
        }
    }

    async checkEquivalentPrescriptions() {
        // Skip equivalent check if not enabled in configuration
        if (!this.enableEquivalentDrugCheck) {
            this.savePrescription(true);
            return;
        }

        if (!this.prescription.packageID || !this.prescription.patientID) {
            this.savePrescription(true);
            return;
        }

        this.checkingEquivalentDrugs = true;
        try {
            const response = await Axios.get<EquivalentPrescription[]>(
                '/Prescription/EquivalentPrescriptions',
                {
                    params: {
                        patientId: this.prescription.patientID,
                        packageId: this.prescription.packageID,
                        drugSource: this.prescription.drugSource,
                        storeId: this.prescription.storeID,
                        rxNumber: this.prescription.rxNumber,
                    },
                }
            );

            if (response.data.length > 0) {
                this.equivalentPrescriptionsData = response.data;  // Store data to pass to modal
                this.showEquivalentModal = true;  // Only show if equivalents exist
            } else {
                this.savePrescription(true);  // Skip directly to save
            }
        } catch (error) {
            console.error('Error checking equivalents:', error);
            this.savePrescription(true);  // On error, proceed with save
        } finally {
            this.checkingEquivalentDrugs = false;
        }
    }

    onEquivalentDiscontinued(result: any) {
        this.equivalentPrescriptionsData = [];
        this.savePrescription(true);
    }

    onEquivalentSkipped() {
        this.equivalentPrescriptionsData = [];
        this.savePrescription(true);
    }

    modalSaveAndResolve(_dispenseErrorId: number) {
        if (this.wasResolved) {
            // Mark the error as resolved.
            Axios.post(`DispenseError/resolve/${this.dispenseError.id}`)
                .then((response) => {
                    this.dispenseError = Object.assign(new DispenseError(), response.data);
                    console.log("Error was marked as resolved", this.dispenseError);

                    this.fetchDispenseError();
                    this.savePrescription(true);
                })
                .catch((error) => {
                    const errorMsg = "Error while resolving " + this.dispenseErrorLabel;
                    this.$notification(NotificationOptions.error(error));
                    console.log(errorMsg, {
                        error,
                        response: error?.response,
                    });
                })
                .finally(() => {
                    this.$bvModal.hide("modal_save_confirmation");
                });
        } else {
            this.savePrescription();
            this.$bvModal.hide("modal_save_confirmation");
        }
    }

    updateSaveButtonLabel() {
        let firstAction = "";
        let secondAction = "";
        let mainAction = "Save changes";
        if (this.errorNote.body && this.wasResolved) {
            firstAction = ", add note and";
            secondAction = "resolve";
        } else if (this.errorNote.body || this.wasResolved) {
            mainAction += " and ";
            if (this.errorNote.body) secondAction = "add note ";
            if (this.wasResolved) secondAction = "resolve";
        } else {
            this.saveButtonLabel = mainAction;
        }

        this.saveButtonLabel = `${mainAction}${firstAction} ${secondAction}`;
    }

    noteChanged(newNote: string) {
        this.errorNote.body = newNote;
        this.updateSaveButtonLabel();
    }

    @Watch("wasResolved") wasResolvedChanged() {
        this.updateSaveButtonLabel();
    }

    /**
     * finish: If true, it indicates to finish creating new Rx for the same script image coming from the ingestion.
     * billPlanCallback: Function callback sent from InsuranceBillingComponent that allows us to save prescription before billing an insurance.
     * goNextRx: If true, the prescription is saved and the next Rx stored in this.prescriptionsInBatch will be loaded in the form.
     */
    savePrescription(
        finish: boolean = false,
        billPlanCallback: Function | null = null,
        goNextRx: boolean = false,
    ) {
        if (!this.prescription || this.saving) return;

        // Check if the patient is deceased when creating a new prescription or refill
        if (this.patient?.deceased && (this.newMode || this.newRefillMode)) {
            this.$notification(NotificationOptions.error('Cannot save prescriptions for deceased patients.'));
            return;
        }

        if (this.drug instanceof Drug) {
            this.prescription.productNameShort = this.drug.productNameLong;
        }
        this.prescription.quantity = this.prescription.quantity || 0;
        this.prescription.dispensed = this.prescription.dispensed || 0;
        this.assignViewValues();
        if (this.queueItemID && !this.prescription.rxNumber) {
            this.saving = true;
            this.addLockWithURL(`/IngestionQueue/${this.queueItemID}/lock`, 60000)
                .then((_result) => {
                    this.saving = false;
                    this.saveAfterCheck(finish, billPlanCallback, goNextRx);
                })
                .catch((error) => {
                    if (error.response && error.response.status == 418) {
                        console.log(`QueueItem is locked`);
                        console.log(error.response.data);
                        this.showLockedMessage(
                            EntityType.IngestionQueueItem,
                            this.queueItemID!.toString(),
                            this.queueItemID!.toString(),
                            error.response.data,
                        );
                    } else {
                        this.$notification(NotificationOptions.error(error));
                    }
                    this.saving = false;
                });
            //do lock check,
            //then do save
        } else {
            this.saveAfterCheck(finish, billPlanCallback, goNextRx);
        }
    }

    popQueue() {
        if (!(this.$store.queueItems?.length ?? 0)) return false;

        const nextItem = this.$store.queueItems?.pop();

        // Build query object, only including properties with actual values
        const query: Record<string, string> = {};
        if (nextItem?.imageID) query.imageID = String(nextItem.imageID);
        if (nextItem?.escriptID) query.escriptID = String(nextItem.escriptID);
        if (nextItem?.id) query.queueItemID = String(nextItem.id);
        if (nextItem?.programTransferId) query.programTransfer = String(nextItem.programTransferId);

        this.$router.replace({
            name: "Prescription",
            query,
        });

        return true;
    }

    async saveAfterCheck(
        finish: boolean = false,
        billPlanCallback: Function | null = null,
        goNextRx: boolean = false,
    ) {
        if (this.saving) return;

        if (this.prescription.rxID != "--") {
            let dateNow: Date = new Date();
            let dateFillDate: Date = new Date(this.prescription.fillDate);
            let timeInMilliseconds: number = dateNow.getTime() - dateFillDate.getTime();
            let daysBetweenDates: number = Math.ceil(timeInMilliseconds / (1000 * 60 * 60 * 24));
            if (
                !this.isUserPrescriptionAdmin &&
                daysBetweenDates > 15 &&
                this.prescription.fillDate != this.originalFillDate
            ) {
                this.$bvModal.msgBoxOk(`You cannot change the fill date to older than 14 days ago.`);
                return;
            }
        }

        if (this.showDeaAlert) {
            try {
                const deaResponse = await this.$bvModal.msgBoxConfirm("The prescriber has no DEA number assigned and this prescription is for a controlled substance. Continue saving?");
                if (!deaResponse) return;
            }
            catch {

            }
        }

        this.saving = true;
        if (this.getErrorValidation()) {
            Axios.post("/Prescription/", { ...this.prescription, fillDate: moment(this.prescription.fillDate).format('YYYY-MM-DD'), writtenDate: moment(this.prescription.writtenDate).format('YYYY-MM-DD'), dispensingItems: this.localDispenseItems }, {
                params: { createNewRefill: this.newRefillMode },
            })
                .then((response) => {
                    this.prescription = Object.assign(new Prescription(), response.data);
                    this.$notification(
                        NotificationOptions.successSaveNotificationPreset(
                            `Prescription ${this.prescription.rxID}`,
                        ),
                    );

                    // Capture queueItemID before route change to prevent it from becoming undefined
                    // Falls back to savedIngestionQueueItemID which persists across multiple save operations
                    const savedQueueItemID = this.queueItemID || this.savedIngestionQueueItemID;

                    new Promise(async (resolve, reject) => {
                        /**
                         * When on an Ingestion, the route will be replaced by the newly created RX
                         * and will push a new URL on the browsing history for every new RX created
                         * from the same image.
                         */
                        if (!this.isBatchMode && this.fromIngestion && finish) {
                            const currentRoute = this.$route;
                            const route = {
                                name: "PrescriptionDetails",
                                params: {
                                    storeID: this.prescription.storeID.toString(),
                                    rxNumber: this.prescription.rxNumber.toString(),
                                    rfNumber: this.prescription.rfNumber?.toString() ?? "",
                                },
                                query: { preserveScroll: "1" },
                            };
                            if (currentRoute.query.queueItemID) {
                                this.$router.replace(route);
                            } else if (currentRoute.name == "PrescriptionDetails") {
                                this.$router.push(route);
                            }
                        }

                        if (
                            savedQueueItemID &&
                            this.prescription &&
                            this.prescription.storeID &&
                            this.prescription.rxNumber &&
                            (this.prescription.rfNumber || 0) >= 0
                        ) {
                            const queueItem: QueueItem = {
                                id: +savedQueueItemID,
                                storeID: +this.prescription.storeID,
                                rxNumber: +this.prescription.rxNumber,
                                rfNumber: +(this.prescription.rfNumber || 0),
                            } as QueueItem;

                            // Complete ingestion
                            Axios.post<QueueItem[]>(`/IngestionQueue/`, [queueItem])
                                .then(async (response) => {
                                    console.log(`IngestionQueue marking complete:`, response);

                                    const navigatedToNextItem = this.stayOnPageAfterSave
                                        ? false
                                        : await this.handleAutoModeCompletion();

                                    resolve({ response, navigatedToNextItem });
                                })
                                .catch((error) => {
                                    const errorMsg = `IngestionQueue marking complete error:`;
                                    console.error(errorMsg, {
                                        error,
                                        response: error?.response,
                                    });
                                    this.$notification(
                                        NotificationOptions.error(error),
                                    );
                                    if (!finish) {
                                        // Don't block Save and New form clearing if ingestion marking fails
                                        resolve({ response: null, navigatedToNextItem: false });
                                    } else {
                                        reject(error);
                                    }
                                });
                        } else {
                            if (this.routeRxId && finish) {
                                if (this.$store.autoModeEnabled) {
                                    const navigatedToNextItem = await this.handleAutoModeCompletion();
                                    resolve({ response: null, navigatedToNextItem });
                                } else {
                                    // If this page was shown by clicking a details button from another page, we go back to the previous page.
                                    this.backToPreviousPage();
                                }
                            } else if (!finish) {
                                // Save and New from existing prescription — no ingestion queue to mark
                                resolve({ response: null, navigatedToNextItem: false });
                            } else {
                                console.error(
                                    "Something went wrong while identifying the case of saving",
                                    {
                                        rxId: this.prescription.rxID,
                                        fromIngestion: this.fromIngestion,
                                        queueItemID: savedQueueItemID,
                                        routeRxId: this.routeRxId,
                                        finish,
                                    },
                                );
                                reject();
                            }
                        }
                    }).then((result) => {
                        if (this.stayOnPageAfterSave) {
                            this.stayOnPageAfterSave = false;
                            // Stay on page - no navigation or form clearing
                        } else if (finish) {
                            const didPopQueueItem = this.popQueue();

                            // Only navigate back if NEITHER auto mode nor legacy queue navigated to next item
                            if (!result?.navigatedToNextItem && !didPopQueueItem) {
                                // when we clear, we need to clear the imageID query if it exists.
                                this.clearQuery();
                                // Back to ingestion queue screen - skip billing validation since we already saved successfully
                                this.backToPreviousPage(true);
                            }
                        } else if (!finish && !billPlanCallback) {
                            // Save and New — handles both ingestion and non-ingestion flows.
                            // Keep the Store, patient, doctor, and image/escript information the same.
                            const continueSameImage = true;
                            const preservedOriginCode = this.originCode;
                            const preservedImageID = this.prescription.imageID;
                            const preservedEscriptID = this.prescription.escriptID;
                            const preservedAddNote = this.keepHardcopyNote ? this.addNote : "";
                            this.clearForm(continueSameImage);
                            // Do NOT call mount() here: when the page was opened from an escript
                            // or program transfer, mount() kicks off an async createPrescription*
                            // that overwrites this.prescription/patient/doctor when it resolves —
                            // clobbering the preserved IDs set below and causing the validator to
                            // falsely report Patient/Doctor as missing on the next Save.
                            this.newMode = true;

                            this.prescription.storeID = Number(this.store.id);
                            this.prescription.patientID = Number(this.patient.id);
                            this.prescription.prescriberID = Number(this.doctor.id);
                            this.originCode = preservedOriginCode;
                            this.addNote = preservedAddNote;

                            // Restore the image from the queue item or from the saved prescription
                            if (this.queueItem?.imageID) {
                                this.prescription.imageID = this.queueItem.imageID;
                            } else if (preservedImageID) {
                                this.prescription.imageID = preservedImageID;
                            }

                            // Restore escript ID if present (for multi-order escripts)
                            if (preservedEscriptID) {
                                this.prescription.escriptID = preservedEscriptID;
                            }

                            const message = this.keepHardcopyNote && preservedAddNote
                                ? "You can start introducing information for the new RX with the same image. Hardcopy note has been retained."
                                : "You can start introducing information for the new RX with the same image.";
                            this.$notification(
                                NotificationOptions.notificationOptionsPreset(
                                    message,
                                    NotificationOptions.NotificationTypes.info,
                                ),
                            );
                        }
                    });

                    //Prevent redirection to the previous page after submitting answer to custom questions:
                    // - Saving RX was triggered by billing component
                    // - Finish is false which means we continue with the same RX image
                    // - If we are in a refill batch, and we need to go to the next RX.
                    const preventRedirection = !!billPlanCallback || !finish || goNextRx || this.stayOnPageAfterSave;
                    this.submitQuestionaries(this.prescription, preventRedirection);

                    //If the Next button was clicked
                    if (goNextRx) {
                        if (this.willAutorefill) {
                            this.$emit("batchFinished", this.prescription);
                        }
                        this.nextRx();
                        return;
                    }

                    // Don't reset newMode for Save and New — the inner .then() will set it to true
                    // after clearing the form for the next prescription
                    if (finish || billPlanCallback) {
                        this.newMode = false;
                        this.newRefillMode = false;
                    }

                    if (billPlanCallback) {
                        billPlanCallback(this.prescription);
                        return;
                    }
                })
                .catch((error: AxiosError) => {
                    this.stayOnPageAfterSave = false;
                    if (billPlanCallback) billPlanCallback(null, true);

                    // Handle duplicate prescription from same image
                    if (error.response?.status === 409 && error.response?.data?.error === 'DUPLICATE_IMAGE_PRESCRIPTION') {
                        this.$bvModal.msgBoxOk(
                            'This prescription has already been created from this image by another user. Please close this page and check the existing prescription.',
                            { title: 'Duplicate Prescription', okVariant: 'warning' }
                        );
                        return;
                    }

                    this.$notification(NotificationOptions.error(error));
                })
                .finally(() => {
                    this.saving = false;
                });
        }
    }

    getErrorValidation() {
        if (this.prescription.daySupply === 0) {
            return true;
        }

        if (this.validationClasses.daySupply === "has-danger") {
            this.$nextTick(() => {
                this.$notification(NotificationOptions.error("Enter valid value for days supply"));
                this.saving = false;
            });
            return false;
        }

        if (this.validationClasses.refills === "has-danger") {
            this.$nextTick(() => {
                this.$notification(NotificationOptions.error("Enter valid value for refills"));
                this.saving = false;
            });
            return false;
        }

        return true;
    }

    clearQuery() {
        // when we clear, we need to clear the imageID query if it exists.
        if (Object.keys(this.$route.query).length) {
            console.log("clearing query");
            this.$router.replace({
                name: this.$route.name,
                query: {},
            } as Location);
        }
    }

    onPatientCreated(newPatient: Patient) {
        this.editPatientForm = false;
        this.showPatientForm = false;
        this.patient = newPatient;
        this.$bvModal.hide("create-patient");
    }

    onDoctorCreated(newDoctor: Prescriber) {
        this.showDoctorForm = false;
        this.editDoctorForm = false;
        this.doctor = newDoctor;
        this.$bvModal.hide("create-doctor");
    }

    showPrescriptionForm(close?: string) {
        switch (close) {
            case "hidePatientForm":
                this.editPatientForm = false;
                this.showPatientForm = false;
                this.patient = new Patient();
                break;
            case "hideDoctorForm":
                this.showDoctorForm = false;
                this.editDoctorForm = false;
                this.doctor = new Prescriber();
                break;
        }
        this.showDocuStore = false;
    }

    replaceRouterParamsWithCurrentPrescription() {
        if (this.routeRxId == null) return;

        if (!isNaN(Number(this.$route.params.storeID))) {
            this.$router.replace({
                name: "PrescriptionDetails",
                params: {
                    rfNumber: this.prescription.rfNumber?.toString() ?? "",
                    rxNumber: this.prescription.rxNumber?.toString() ?? "",
                    storeID: this.prescription.storeID.toString(),
                },
                query: { preserveScroll: "1" },
            });
            window.location.reload();
            return;
        }
        if (this.$route.params.rxId) {
            this.$router.replace({
                name: "PrescriptionDetailsId",
                params: { rxId: this.prescription.rxID },
                query: { preserveScroll: "1" },
            });
            window.location.reload();
            return;
        }
    }

    transferRx() {
        console.log("route: ", this.$route);
        Axios.post(`/Store/${this.newStore?.id}/transfer`, this.prescription)
            .then((response) => {
                this.prescription = Object.assign(new Prescription(), response.data);

                this.$notification(
                    NotificationOptions.successSaveNotificationPreset("Prescription Transferred"),
                );

                this.replaceRouterParamsWithCurrentPrescription();
            })
            .catch((error: AxiosError) => {
                if (error) {
                    this.$notification(
                        NotificationOptions.errorSaveNotificationPreset("Internal Transfer", error),
                    );
                    console.error("Internal Transfer - ", {
                        error,
                        response: error?.response,
                    });
                }
            });
        this.$bvModal.hide("transfer-rx");
    }

    equivalentDrugsSearched(drugs: any) {
        this.equivalentDrugs = drugs;
    }

    save() {
        this.stayOnPageAfterSave = false;
        if (this.isBatchMode) {
            this.saveAndNext();
        } else {
            this.saveAndFinish();
        }
    }

    cancel() {
        if (this.isBatchMode) {
            this.nextRx();
        } else {
            this.backToPreviousPage();
        }
    }

    //**BATCH MODE FUNCTIONS */
    saveAndNext() {
        this.savePrescription(true, null, true);
    }

    nextRx() {
        const thisRx = this.prescription;
        if (this.batchIndex >= 0) {
            this.clearForm();
            this.$notification(
                NotificationOptions.notificationOptionsPreset(
                    `Next Rx in batch...`,
                    NotificationOptions.NotificationTypes.info,
                ),
            );
        }

        if (!this.isBatchOver) {
            this.batchIndex++;
            const nextRxId = Object.assign(
                new Prescription(),
                this.prescriptionsInBatch[this.batchIndex],
            ).rxID;
            this.getPrescription(nextRxId, this.refillBatchMode);
            this.scrollToBeginning();

            if (this.isBatchOver) {
                this.buttonTitles.save = "Save and Finish";
                this.buttonTitles.cancel = "";
            }
        } else {
            this.$notification(
                NotificationOptions.notificationOptionsPreset(
                    `Batch was finished.`,
                    NotificationOptions.NotificationTypes.info,
                ),
            );
            this.$emit("batchFinished", thisRx);
        }
    }

    scrollToBeginning() {
        const switchId = "rx-modal___BV_modal_header_";
        const switch_dom = document.getElementById(switchId);
        if (switch_dom) switch_dom.scrollIntoView({ block: "start" });
    }

    //**CUSTOM QUESTIONS METHODS*/
    fetchDrugCategory(productID: number | string = 0) {
        if (!(this.drug?.drugId || productID)) return;

        productID = productID || this.drug?.drugId;
        Axios.get<Array<DrugCategory>>(`Drug/${productID}/${this.drug.source}/categories`)
            .then((response) => {
                this.drugCategories = response.data.map((r) =>
                    Object.assign(new DrugCategory(), r),
                );
            })
            .catch((error) => {
                console.error("Error while getting drug categories", {
                    error,
                    response: error?.response,
                });
            });
    }

    questionarySaved(_categoryId: number, preventRedirection: boolean = false) {
        if (!(this.globalPreventRedirection || preventRedirection)) {
            this.globalPreventRedirection = true;
            this.backToPreviousPage();
        }
    }

    handleSplitComplete(result: { newQueueItems: QueueItem[]; originalScriptUsed: boolean; newImageId: number | null }) {
        console.log("Split complete:", result);

        if (result.originalScriptUsed && result.newImageId) {
            console.log("Original Script used, refreshing with new image:", result.newImageId);
            this.prescription.imageID = result.newImageId;
            // If we have a queue item, update it too
            if (this.queueItem) {
                this.queueItem.imageID = result.newImageId;
            }
            this.$notification(NotificationOptions.success("Document split successfully. The selected pages have been kept with this prescription.", "Split Complete"));
            return;
        }

        const queueItems = result.newQueueItems;
        if (!queueItems || queueItems.length === 0) {
            this.backToPreviousPage(true);
            return;
        }

        if (this.$store.queueItems?.length ?? 0 > 0) {
            this.$store.queueItems?.push(...queueItems);
        } else {
            this.$store.queueItems = queueItems;
        }
        this.popQueue();
    }

    redrawEscript() {
        const escriptId = this.escriptID || this.savedEscriptID;
        if (!escriptId) {
            console.error('No escriptID available for redraw');
            return;
        }
        Axios.get(`Escript/Redraw/${escriptId}`)
            .then((resp) => {
                console.log("escript Redraw response: ", resp);
                window.location.reload();
            })
            .catch((_err) => {
            });
    }

    submitQuestionaries(rx: Prescription, preventRedirection: boolean = false) {
        const refs = this.$refs;
        this.drugCategories?.forEach((cat) => {
            const comp = (refs[`questionary_${cat.id}`] as Array<any>)[0] as QuestionaryComponent;
            if (comp.save) comp.save(rx, preventRedirection);
        });
    }

    //Patient Notes
    async refreshPatientNotes(_newNote: Note) {
        if (this.$refs.patientNotesList instanceof NotesList) {
            await (this.$refs.patientNotesList as NotesList).loadNotes();
        }
    }

    openRxHistory() {
        const modal = this.$refs["patient_rx_history_modal"] as PatientRxHistoryModalComponent;
        if (modal) {
            modal.openRxHistory();
        } else {
            console.error("Reference dont exist for the component", modal);
        }
    }

    @Watch("prescription")
    prescriptionUpdated(rx: Prescription) {
        if (
            rx.programName?.includes("COVID") &&
            rx.updatedBy == "PatientWeb" &&
            this.notAllowNewRefill &&
            this.refillBatchMode
        ) {
            console.log(
                "prescription's programName includes 'COVID' and was updated by PatientWeb. Lets skip it.",
            );
            this.nextRx();
        }
    }

    @Watch("expiration")
    expirationDateUpdate(val: Date, _oldVal: Date) {
        // noinspection PointlessBooleanExpressionJS,SuspiciousTypeOfGuard
        if (!(val instanceof Date) || isNaN(val as any)) return;
        this.validationClasses.expirationDate = val ? "has-success" : "has-danger";
    }

    @Watch("prescription.fillDate")
    @Watch("expiration")
    @Watch("writtenDate")
    fillDateUpdate() {
        const fillDate = this.prescription.fillDate;
        // Don't apply validation styling until both dates are available
        if (!fillDate || !this.expiration) {
            this.validationClasses.fillDate = "";
            return;
        }
        const beforeExpiration = moment(fillDate).isSameOrBefore(this.expiration);
        const notBeforeWritten = !this.writtenDate || moment(fillDate).isSameOrAfter(moment(this.writtenDate), "day");
        const valid = beforeExpiration && notBeforeWritten;
        this.validationClasses.fillDate = valid ? "has-success" : "has-danger";
    }

    @Watch("prescription.daySupply")
    daySupplyUpdate(val: number, _oldVal: number) {
        this.validationClasses.daySupply = val && Number.isInteger(val) ? "has-success" : "has-danger";
    }

    @Watch("prescription.refills")
    refillsUpdate(val: number, _oldVal: number) {
        this.validationClasses.refills = val != null && Number.isInteger(val) ? "has-success" : "has-danger";
    }

    @Watch("dispensedQuantity")
    dispenseUpdate(val: number) {
        if (val == 0) {
            this.validationClasses.dispense = "has-warning";
            return;
        }
        this.validationClasses.dispense = val ? "has-success" : "has-danger";
    }

    @Watch("prescription.quantity")
    quantityUpdate(val: number) {
        if (val == 0) {
            this.validationClasses.quantity = "has-warning";
            return;
        }
        this.validationClasses.quantity = val ? "has-success" : "has-danger";
    }

    updateAlerts() {
        this.entityAlertsComponent?.refresh();
    }

    startSurescriptsElectronicPriorAuthorization() {
        Axios.post("PriorAuthorization/Create/Surescripts", this.prescription)
            .then((resp) => {
                console.log(resp);
                this.$notification(
                    NotificationOptions.successSaveNotificationPreset(
                        "Electronic Prior Authorization",
                    ),
                );
            })
            .catch((err) => {
                this.$notification(
                    NotificationOptions.errorSaveNotificationPreset(
                        "Electronic Prior Authorization",
                        err,
                    ),
                );
            });
    }

    startSurescriptsElectronicPriorAuthorizationExpedited() {
        Axios.post("PriorAuthorization/Create/Surescripts/Expedited", this.prescription)
            .then((resp) => {
                console.log(resp);
                this.$notification(
                    NotificationOptions.successSaveNotificationPreset(
                        "Electronic Prior Authorization",
                    ),
                );
            })
            .catch((err) => {
                this.$notification(
                    NotificationOptions.errorSaveNotificationPreset(
                        "Electronic Prior Authorization",
                        err,
                    ),
                );
            });
    }

    onPharmacyFormClose() {
        this.showPharmacyForm = false;
        this.originPharmacy = new PharmacyRecord();
    }

    onPharmacyCreate(newPharma: PharmacyRecord) {
        this.showPharmacyForm = false;
        this.originPharmacy = new PharmacyRecord(newPharma);
    }

    contactTypeFormatter(value: Number): string {
        switch (value) {
            case 0:
                return "Text";
            case 1:
                return "Phone";
            case 2:
                return "Email";
            case 3:
                return "Auto Outbound";
            case 4:
                return "Auto Fax";
        }
        return "Unknown";
    }

    get prescriptionContactFields() {
        return [
            {
                key: "contactType",
                formatter: this.contactTypeFormatter,
            },
            {
                key: "contactDate",
                formatter: (value: Date) => moment(value).format("l LT"),
                sortable: true,
            },
            "contactMessage",
        ];
    }

    async prescriptionContacts(_ctx: BvTableCtxObject) {
        try {
            const response = await Axios.get<Array<Prescription>>(`/api/Prescription/${this.prescription.rxID}/contacts`);
            return response.data.map((r) => new Prescription(r));
        } catch (error) {
            return [];
        }
    }

    async openDocument(pd: PatientDocument) {
        try {
            const res = await Axios.get(`image/${pd.imageId}`, { responseType: "blob" });
            const objectUrl = URL.createObjectURL(res.data);
            window.open(objectUrl, '_blank');
        } catch (err) {
            console.error("Error while getting image to show.", err);
            this.$notification(NotificationOptions.error(err));
        }
    }

    @Watch('prescription')
    @Watch('patient.primaryLanguage')
    @Watch('prescription.directions')
    @Watch('drugNdc')
    @Watch('drug.productNameShort')
    async fetchTranslations() {
        this.directionsTranslations = {} as any;
        if (this.patient.primaryLanguage == 0) return;
        if (!(this.prescription.rxNumber > 0)) return;
        if (this.prescription.isExpired) return;
        if (!this.drugNdc) return;
        if (!this.drug.productNameShort) return;

        try {
            this.loadingDirections = true;
            const resp = await Axios.get(`/api/SigTranslation/Translations`, {
                params: {
                    directions: this.prescription.directions,
                    drugName: this.drug.productNameShort,
                    ndc11: this.drugNdc,
                },
            });
            this.directionsTranslations = resp.data;
        } catch (err) {
            // ignore
        }
        this.loadingDirections = false;
    }

    @Watch('prescription')
    handleDispenseItemsChange() {
        const hasDispenseItems = !!this.prescription?.dispensingItems?.length;
        if (hasDispenseItems) {
            this.localDispenseItems = [...this.prescription.dispensingItems!];
            return;
        }

        const hasLegacyDispenseData = this.prescription.serialNumber || this.prescription.lotNumber || this.prescription.drugExpirationDate;
        if (hasLegacyDispenseData) {
            this.localDispenseItems = [
                {
                    lotNumber: this.prescription.lotNumber,
                    serialNumber: this.prescription.serialNumber,
                    expirationDate: this.prescription.drugExpirationDate,
                    quantity: null,
                },
            ];
        } else {
            this.localDispenseItems = [];
        }
    }

    async translateDirections() {
        if (!this.selectedTranslation) {
            this.$notification(NotificationOptions.error("Select directions to translate."));
            return;
        }
        this.loadingDirections = true;
        try {
            const config = {
                params: {
                    directions: this.prescription.directions,
                    patientId: this.patient.id,
                },
            };
            const resp = await Axios.get<string>(`/api/SigTranslation/Translations/${this.selectedTranslation}`, config);
            const translation = this.directionsTranslations.paraphrases.find(p => p.id == this.selectedTranslation);
            const baseDirections = translation?.token ?? this.prescription.directions;
            const directionLines = [];
            if (baseDirections) {
                directionLines.push(`Directions Translated From: ${baseDirections}`);
            }
            if (this.prescription.memo) {
                directionLines.push(this.prescription.memo);
            }
            this.prescription.memo = directionLines.join("\n");
            this.prescription.directions = resp.data;
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        }
        this.loadingDirections = false;
    }

    @Watch('doctor')
    async fetchDoctorProgram() {
        if (!this.doctor?.programId) {
            this.doctorProgram = new Program();
            return;
        }

        try {
            const response = await Axios.get<Program>("/api/Program/" + this.doctor.programId);
            this.doctorProgram = Object.assign(new Program, response.data);
        } catch {
            this.doctorProgram = new Program();
        }
    }

    autoselectStore(options: Array<Store>) {
        const activeStores = options.filter(o => o.active);
        return (activeStores.length !== 1) ? undefined : activeStores[0];
    }

    async createPAPApplication() {
        const confirmed = await this.$bvModal.msgBoxConfirm("Create a PAP application from this script?", {
            title: 'Create PAP Application',
            size: 'sm',
            buttonSize: 'sm',
            okVariant: 'primary',
            okTitle: 'Create Application',
            cancelTitle: 'Cancel',
            footerClass: 'p-2',
            centered: true,
        });

        if (!confirmed) return;

        try {
            await this.$http.post(`/PatientAssistanceProgram/CreateFromScript/${this.prescription?.rxID}`);
            this.$notification(NotificationOptions.success("PAP created successfully."));
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
            console.error(err);
        }
    }

    protected customContactPrescriberTemplates: PrescriberContactTemplate[] = [];
    private customContactPrescriberTemplate: PrescriberContactTemplate | null = null;

    async getPrescriberCustomContactTemplates() {
        try {
            const resp = await Axios.get("/Prescriber/contact-templates/true");
            this.customContactPrescriberTemplates = resp.data;
        } catch (err) {
            console.error(err);
        }
    }

    get filteredCustomContactPrescriptionTemplates() {
        return this.customContactPrescriberTemplates.filter(t => {
            if (!t.programs.length) return true;
            return t.programs.find(p => p.id === this.program?.id);
        });
    }

    protected dispenseErrorLabel = "";

    async fetchDispenseErrorLabel() {
        try {
            const resp = await this.$http.get("/Configuration/DispenseErrorLabel");
            this.dispenseErrorLabel = resp.data?.trim();
        } catch {
            this.dispenseErrorLabel = "Error";
            //ignore
        }
    }

    protected localDispenseItems: PrescriptionDispenseItem[] = [];

    get dispenseItems() {
        if (this.disableFields) return this.prescription.dispensingItems;
        return [...this.localDispenseItems, {}];
    }

    updateDispenseItem(key: string, index: number, value: any) {
        if (this.localDispenseItems.length === index) {
            this.localDispenseItems.push({});
        }

        const item = this.localDispenseItems[index];
        (item as any)[key] = value;

        this.localDispenseItems = this.localDispenseItems
            .filter(di => di.lotNumber || di.serialNumber || di.expirationDate || di.quantity);
    }

    onTransferCompleted(transferData: any) {
        if (transferData?.prescription) {
            this.prescription = Object.assign(new Prescription(), transferData.prescription);
            this.fetchPrescription(this.prescription);
        }

        this.updateAlerts();
    }

    async markAsNotAScript() {
        if (!this.queueItemID) {
            return;
        }

        try {
            // Create queue item with only the ID (no storeID/rxNumber/rfNumber = "Not A Script")
            const queueItem: QueueItem = {
                id: this.queueItemID
            };

            // Mark as complete (not a script)
            await Axios.post<QueueItem[]>(`/IngestionQueue/`, [queueItem]);

            this.$notification({
                message: 'Item marked as "Not A Script"',
                timeout: 3000,
                icon: "now-ui-icons ui-1_check",
                horizontalAlign: "center",
                verticalAlign: "top",
                type: "success"
            });

            if (this.popQueue()) {
                // Successfully popped to next queued item
                return;
            }

            const hasNextItem = await this.handleAutoModeCompletion();

            if (!hasNextItem && this.fromIngestion) {
                this.backToPreviousPage(true);
            }
        } catch (error) {
            console.error('Error marking as not a script:', error);
            this.$notification(NotificationOptions.error(error));
        }
    }

    async handleAutoModeCompletion(): Promise<boolean> {
        return await this.handleAutoModeNextItem();
    }

    // MedSync Integration Methods
    handleViewMedSyncDetails() {
        if (this.medSyncDetailsModal) {
            this.medSyncDetailsModal.show();
        }
    }

    handleAddToMedSync(data: { enrollmentId: number | null; rxId: string | null }) {
        if (this.addToMedSyncModal && data.rxId) {
            this.addToMedSyncModal.show(data.rxId);
        }
    }

    async handleMedSyncAdded(data: { rxId: string; enrollmentId: number }) {
        // Refresh the banner to show updated medication count
        if (this.medSyncBanner) {
            await this.medSyncBanner.refresh();
        }
    }
}
</script>

<style lang="scss">
.office-contacts-popover .card:last-child {
    margin-bottom: 0;
}
</style>

<style scoped lang="scss">
/*noinspection CssUnusedSymbol*/
::v-deep .modal-dialog {
    right: 20px;
    position: absolute;
    min-width: 600px;
}

#prescription-image {
    min-height: 880px;

    .actions {
        display: grid;
        gap: 10px;
        grid-template-columns: repeat(auto-fit, minmax(calc(min(100%, 200px)), 1fr));
        margin-bottom: 10px;

        button {
            padding-left: 4px;
            padding-right: 4px;

            &:disabled {
                pointer-events: all;
            }
        }
    }
}

.image-wrapper {
    position: sticky;
    top: 0;
}

.script-image {
    height: 100vh;
    border: 1px solid black;
}

table.dispense-items ::v-deep(th) {
    font-weight: normal !important;
}
</style>
